/**
 * Kleine Ergaenzungen am Kauffenster der Produktseite.
 *
 * Die Beschreibung steckt in einem aufklappbaren Bereich. Auf dem Handy soll er
 * zugeklappt beginnen - dort ist die Hoehe knapp. Am Rechner ist Platz genug,
 * dort steht die Beschreibung offen und die Zeile zum Aufklappen entfaellt
 * (ausgeblendet per CSS).
 *
 * Ein <details> laesst sich nicht per CSS oeffnen, deshalb diese wenigen Zeilen.
 */
import { sperreUm } from '@theme/dp-laden';

const BREIT = window.matchMedia('(min-width: 750px)');

/** Luft zwischen der Unterkante des Menues und dem Kauffenster. */
const LUFT = 8;

/** Abstand des Kauffensters zum linken und rechten Bildschirmrand (wie im Stilblatt). */
const LUFT_SEITE = 12;

/*
 * Luft unter dem Kauffenster bis zum naechsten Abschnitt.
 *
 * Etwas mehr als ueber ihm: nach oben grenzt es an die Bilder, die bis an den
 * Rand laufen, nach unten an einen Kasten mit eigener Flaeche. Der gleiche Wert
 * wirkte dort optisch zu knapp.
 */
const LUFT_NACH_UNTEN = 18;

/** Das Feld, das im schmalen Fenster zuletzt vom Nutzer aufgeklappt wurde - es bleibt beim Zurueckdrehen offen. */
let zuletztGewaehlt = null;

function richteBeschreibung() {
  const bereiche = [...document.querySelectorAll('.dp-kauffenster details')];
  for (const bereich of bereiche) {
    if (BREIT.matches) {
      bereich.open = true;
    } else if (bereich.dataset.dpVomNutzer === undefined) {
      // Nur den Ausgangszustand setzen - wer selbst aufklappt, behaelt es.
      bereich.open = false;
    }
  }
  /*
   * Schmal ist immer hoechstens ein Feld offen (die Reiter, dp-reiter.js).
   * Beim Zurueckdrehen aus dem Querformat standen sonst beide offen
   * untereinander: Das Aufklappen am Rechner und im Querformat geschah ohne
   * den Nutzer, trug aber seine Marke (merkeEingriff). Es bleibt das zuletzt
   * gewaehlte, sonst das erste offene Feld.
   */
  if (!BREIT.matches) {
    const offen = bereiche.filter((bereich) => bereich.open);
    if (offen.length > 1) {
      const bleibt = offen.includes(zuletztGewaehlt) ? zuletztGewaehlt : offen[0];
      for (const bereich of offen) if (bereich !== bleibt) bereich.open = false;
    }
  }
}

function merkeEingriff(ereignis) {
  const bereich = ereignis.target;
  if (bereich instanceof HTMLElement && bereich.matches('.dp-kauffenster details')) {
    /*
     * Am Rechner gibt es nichts zuzuklappen: Beschreibung und Personalisierung
     * stehen dort offen. Die Kopfzeile der Personalisierung ist zwar zu sehen,
     * sie ist aber eine Ueberschrift und keine Schaltflaeche.
     */
    if (BREIT.matches && !bereich.open) {
      bereich.open = true;
      return;
    }

    /*
     * Am Rechner und im Querformat klappt das Fenster die Felder selbst auf
     * (richteBeschreibung) - das ist keine Wahl des Nutzers und traegt keine
     * Marke, sonst bliebe beim Drehen ins Hochformat alles offen.
     */
    if (!BREIT.matches) {
      bereich.dataset.dpVomNutzer = '';
      if (bereich.open) zuletztGewaehlt = bereich;
    }

    /*
     * Auf- und Zuklappen aendert die Hoehe des Fensters. Der Beobachter meldet
     * das zwar auch, aber das Ereignis ist der direktere Weg - und es gibt
     * Umgebungen, in denen ResizeObserver nicht anspringt.
     */
    reserviere();
    haltAmBildende();

    // Das Feld aendert die Hoehe - danach kann mehr oder weniger zu sehen sein.
    window.setTimeout(pruefeAnzeiger, 260);
  }
}

/*
 * Zweite Aufgabe: dem aufgeklappten Menue Platz machen.
 *
 * Das Kauffenster haftet unter dem Header. Klappt das Menue auf, liegt es
 * darueber - und weil das Fenster bewusst unter dem Header rangiert, wuerde es
 * dahinter verschwinden. Statt es hochzustufen rutscht es nach unten: es bleibt
 * vollstaendig sichtbar und das Menue behaelt seinen Platz.
 *
 * Gemessen wird die Unterkante des offenen Menues gegen die des Headers. Feste
 * Werte waeren falsch, sobald eine Kategorie eine Zeile mehr hat.
 */
function schiebe() {
  const spalte = document.querySelector('.product-details');
  if (!spalte) return;

  if (!BREIT.matches) {
    spalte.style.removeProperty('--dp-menue-schub');
    return;
  }

  const offen = [...document.querySelectorAll('.header .menu-list__submenu')].find(
    (feld) => !feld.hasAttribute('inert')
  );

  if (!offen) {
    spalte.style.removeProperty('--dp-menue-schub');
    return;
  }

  /*
   * Gerechnet wird gegen die Hafthoehe, nicht gegen die Unterkante des Headers.
   * Beides faellt nur zusammen, solange der Header mitgescrollt ist - ganz oben
   * auf der Seite haftet das Fenster noch bei null und muesste sonst zu wenig
   * ausweichen.
   */
  const roh = getComputedStyle(spalte).getPropertyValue('--sticky-header-offset').trim();
  const basis = parseFloat(roh) || 0;
  const port = document.querySelector('.page-wrapper');
  const portOben = port ? port.getBoundingClientRect().top : 0;

  const schub = Math.max(0, unterkante(offen) - portOben - basis + LUFT);
  spalte.style.setProperty('--dp-menue-schub', Math.round(schub) + 'px');
}

/**
 * Tiefster sichtbarer Punkt des offenen Menues.
 *
 * Die Huelle des Untermenues ist nicht immer der ganze Kasten: die Spalten
 * darin reichen ein Stueck weiter nach unten als ihr Rahmen. Wer nur die Huelle
 * misst, laesst das Kauffenster zu wenig ausweichen und ein Rest bleibt
 * verdeckt. Deshalb zaehlt die tiefste Kante, die zu finden ist.
 *
 * @param {Element} feld
 * @returns {number}
 */
function unterkante(feld) {
  let tiefste = feld.getBoundingClientRect().bottom;

  for (const teil of feld.querySelectorAll('.menu-list__submenu-inner, .mega-menu, .mega-menu__grid')) {
    tiefste = Math.max(tiefste, teil.getBoundingClientRect().bottom);
  }

  return tiefste;
}

/*
 * Das Menue faehrt auf, es steht also nicht sofort in voller Hoehe da. Eine
 * einzelne Messung im Moment des Oeffnens faellt deshalb zu klein aus. Fuer
 * die Dauer der Bewegung wird nachgemessen; danach steht der Wert.
 */
let nachlauf = 0;

function schiebeNach() {
  cancelAnimationFrame(nachlauf);

  const bis = performance.now() + 700;
  const schritt = () => {
    schiebe();
    if (performance.now() < bis) nachlauf = requestAnimationFrame(schritt);
  };
  nachlauf = requestAnimationFrame(schritt);
}

/*
 * Das Menue meldet seinen Zustand ueber das inert-Attribut. Ein Beobachter ist
 * hier die verlaesslichste Quelle - er greift unabhaengig davon, ob per Maus,
 * Tastatur oder Skript geoeffnet wurde.
 */
function beobachteMenue() {
  const felder = document.querySelectorAll('.header .menu-list__submenu');
  if (!felder.length) return;

  const waechter = new MutationObserver(() => schiebeNach());
  for (const feld of felder) {
    waechter.observe(feld, { attributes: true, attributeFilter: ['inert'] });
  }
}

/*
 * Auf dem Handy klebt das Kauffenster am unteren Bildschirmrand. Ohne Halt
 * wandert es ueber die Vorschlaege und die Fusszeile hinweg - beide waeren
 * dann nie ganz zu sehen.
 *
 * Es bekommt deshalb eine Grenze: die Unterkante der Bilder. Sobald die beim
 * Scrollen hoeher steigt als das Fenster, wird der Abstand nach unten so
 * vergroessert, dass beide Kanten aufeinanderliegen. Von da an wandert das
 * Fenster mit dem Inhalt nach oben aus dem Bild.
 *
 * Bewusst ueber den Abstand und nicht ueber transform: ein transform macht das
 * Element zur eigenen Kulissenwurzel, und der Weichzeichner der Flaeche haette
 * nichts mehr zu zeichnen.
 */
const LUFT_UNTEN = 12;

/**
 * Sicherheitsabstand zur Gestenleiste (--dp-sicher-unten, siehe
 * divineprint-tokens.liquid) - auf Geraeten ohne Leiste null.
 *
 * @returns {number}
 */
function sicherUnten() {
  const wert = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--dp-sicher-unten'));
  return Number.isFinite(wert) ? wert : 0;
}

/** Abstand des Kauffensters zum unteren Bildschirmrand, samt Sicherheitsabstand. */
function luftUnten() {
  return LUFT_UNTEN + sicherUnten();
}

/** Luft zwischen der Unterkante der Bilder und dem angedockten Fenster. */
const LUFT_ZUM_BILD = 12;

/**
 * Die Bilderflaeche der Produktseite.
 *
 * Hat das Produkt noch kein Bild, steht dort keine Galerie, sondern die Kachel
 * mit dem Hinweis darauf (siehe blocks/_product-media-gallery.liquid). Fuer
 * das Kauffenster ist beides dasselbe: die Flaeche, unter der es andockt.
 * Ohne diesen zweiten Fall fand es nichts und blieb am unteren Bildschirmrand
 * kleben.
 *
 * @returns {Element | null}
 */
function holeGalerie() {
  /*
   * Die sichtbare Fassung, nicht die erste im Dokument: stehen mehrere
   * Galerien darin, dockte das Fenster sonst an den Massen einer
   * unsichtbaren an.
   */
  /*
   * Ohne Fotos steht an der Stelle der Galerie die Vorschau der Vorlage:
   * beim 3D-Konfigurator die Live-Vorschau, beim Transferdruck die Kachel mit
   * dem Motiv - letztere aber nur, wenn sie wirklich etwas zeigt. Sonst gilt
   * weiter der Hinweis auf fehlende Bilder. Der Reihe nach gefragt, weil
   * querySelector mit Kommaliste nach Reihenfolge im Baum antwortet, nicht
   * nach Reihenfolge der Auswahl.
   */
  /*
   * Eine Galerie ohne Hoehe zaehlt nicht: ohne Fotos legt das Theme sie
   * trotzdem an, zusammengefallen und ohne Mass. Daran gemessen rutschte das
   * Fenster unter die Kopfzeile.
   */
  const galerie = sichtbareGalerie();
  if (galerie && galerie.getBoundingClientRect().height > 0) return galerie;
  return (
    document.querySelector('.dp-3d-galerie--allein') ||
    /*
     * SOLID: die Live-Vorschau des hochgeladenen Modells (dp-solid.js). Sie
     * steht an der Stelle der Bilder und blendet den Hinweis auf fehlende
     * Bilder aus - ohne sie hier faende das Fenster keine Bildkante und
     * schwebte unten am Rand, genau ueber der Vorschau.
     */
    document.querySelector('.dp-solid-galerie--da') ||
    document.querySelector('.dp-druck-galerie--da') ||
    document.querySelector('.dp-ohne-bild--galerie') ||
    galerie
  );
}


/**
 * Haelt den freien Platz unter den Bildern auf der Hoehe des Kauffensters.
 *
 * Das Fenster schwebt fest ueber der Seite und nimmt im Fluss keinen Platz ein.
 * Damit es nichts verdeckt, haengt die Spalte darunter genau so viel Polsterung
 * an, wie das Fenster hoch ist. Ein fester Wert genuegte nicht: sobald die
 * Beschreibung aufklappt, waechst das Fenster.
 */
function reserviere() {
  const spalte = document.querySelector('.product-details');
  const kauf = document.querySelector('.dp-kauffenster');
  if (!spalte || !kauf) return;

  if (BREIT.matches) {
    spalte.style.removeProperty('--dp-kauf-reserve');
    return;
  }

  /*
   * Der Platz folgt dem Zustand: als Leiste braucht das Fenster nur die
   * Hoehe der Leiste, ganz sein ganzes Mass. Waehrend einer Fahrt gilt
   * schon das Ziel (die Klasse wechselt vor der Fahrt), und das Stilblatt
   * bewegt den Platz mit derselben Dauer - der Abschnitt darunter rueckt
   * also mit dem Fenster, statt zu springen.
   */
  const hoehe = Math.round(istKlein(kauf) ? LEISTE_HOEHE : natuerlicheHoehe(kauf));
  const galerie = holeGalerie();
  if (!galerie) {
    spalte.style.setProperty('--dp-kauf-reserve', hoehe + luftUnten() + LUFT_ZUM_BILD + 'px');
    return;
  }

  /*
   * Unter dem Kauffenster soll genauso viel Luft stehen wie darueber, also der
   * gleiche Abstand wie zur Unterkante der Bilder.
   *
   * Gerechnet wird nicht aus der Hoehe allein: die Produktspalte bringt selbst
   * schon etwas Hoehe mit, und die kaeme sonst obendrauf - der Abschnitt
   * darunter rueckte weiter weg als gewollt. Deshalb wird die Spalte einmal
   * ohne Reserve gemessen und daraus der fehlende Rest bestimmt.
   */
  /*
   * Gerechnet wird gegen die Inhaltskante der Spalte: ihre Unterkante ohne
   * das Polster, das gerade anliegt. Frueher wurde das Polster dafuer kurz
   * auf null gesetzt und dann gemessen - seit es weich faehrt (Stilblatt),
   * folgt es dem Setzen nicht sofort, und die Messung traf den Zwischenwert.
   * Das anliegende Polster abzuziehen ist von der Fahrt unabhaengig.
   */
  const polster = parseFloat(getComputedStyle(spalte).paddingBlockEnd) || 0;
  const inhaltUnten = spalte.getBoundingClientRect().bottom - polster;
  const kaufUnten = galerie.getBoundingClientRect().bottom + LUFT_ZUM_BILD + hoehe;
  const noetig = Math.max(0, Math.round(kaufUnten + LUFT_NACH_UNTEN - inhaltUnten));

  spalte.style.setProperty('--dp-kauf-reserve', noetig + 'px');

  pflegePin();
  pflegeModusZeichen();
}

/** Kuerzer darf das Fenster am Rechner nicht werden, egal wie kurz die Bilder sind. */
const MINDESTHOEHE = 420;

/**
 * Begrenzt das Kauffenster am Rechner auf die Hoehe der Bilderspalte.
 *
 * Die Spalte haftet und kommt am Ende der Bilder zum Stehen - das setzt aber
 * voraus, dass sie ueberhaupt kuerzer ist als die Bilder. Bei Artikeln mit
 * wenigen Bildern und langem Fensterinhalt war sie hoeher, und dann hing das
 * Fenster unten ueber die letzte Bildkante hinaus. Es gab schlicht nichts, an
 * dem es haetten anhalten koennen.
 *
 * Mit dieser Grenze endet es genau auf der Bildkante; was nicht hineinpasst,
 * wandert in den Scrollbereich, den das Fenster ohnehin hat.
 *
 * Auf dem Handy gilt sie nicht - dort schwebt das Fenster ueber den Bildern
 * und richtet sich nach dem Bildschirm.
 */
function begrenzeAufGalerie() {
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf) return;

  if (!BREIT.matches) {
    kauf.style.removeProperty('--dp-kauf-galerie');
    return;
  }

  const galerie = holeGalerie();
  if (!galerie) return;

  /*
   * Auf der 3D-Vorlage ohne Fotos ist die Galerie nur die Vorschau-Kachel
   * (dp-3d-galerie--allein) - flach ohne Modell, eine Kachel hoch mit. An
   * ihr gemessen bliebe das Fenster unnoetig klein und rollte in sich; dort
   * gilt allein die Bildschirmgrenze.
   */
  if (galerie.classList.contains('dp-3d-galerie--allein')) {
    kauf.style.removeProperty('--dp-kauf-galerie');
    return;
  }

  /*
   * Dasselbe auf der Vorlage mit der Druckvorschau: deren Kachel waechst und
   * schrumpft mit dem gewaehlten Format. Haenge das Fenster daran, waere es
   * mal halb so hoch wie der Bildschirm und rollte in sich, obwohl daneben
   * Platz frei ist. Dort gilt allein die Bildschirmgrenze.
   */
  if (document.querySelector('.dp-druck-galerie')) {
    /*
     * Dort gilt nicht die Hoehe der Vorschau - die waechst und schrumpft mit
     * dem Format -, sondern der freie Platz auf dem Bildschirm: unter der
     * Kopfzeile bis zur Luft am unteren Rand. Ganz ohne Grenze stand das
     * Fenster bei hohem Inhalt oben aus dem Bild heraus.
     */
    const kopf = document.querySelector('#header-component');
    const obenAn = kopf instanceof HTMLElement ? Math.max(0, kopf.getBoundingClientRect().bottom) : 0;
    const frei = Math.round(window.innerHeight - obenAn - luftUnten() - LUFT_ZUM_BILD);
    kauf.style.setProperty('--dp-kauf-galerie', Math.max(MINDESTHOEHE, frei) + 'px');
    return;
  }

  const hoehe = Math.round(galerie.getBoundingClientRect().height);
  if (!hoehe) return;

  kauf.style.setProperty('--dp-kauf-galerie', Math.max(MINDESTHOEHE, hoehe) + 'px');
}

/*
 * Die Hoehe des Fensters aendert sich nicht nur beim Drehen des Geraets,
 * sondern auch beim Auf- und Zuklappen der Beschreibung. Ein Beobachter meldet
 * beides.
 */
/*
 * Oeffnet oder schliesst sich die Grossansicht, wechselt das Kauffenster
 * seinen Platz. Diesen einen Weg soll es gleiten - dafuer bekommt es kurz eine
 * Klasse, an der eine Ueberblendung haengt. Dauerhaft duerfte sie nicht
 * gesetzt sein: beim Scrollen aendert sich der Abstand fortlaufend, und eine
 * Ueberblendung liefe der Bewegung dann hinterher.
 */
let gleitZeit = 0;
let gleitet = false;

/** Dauer der Ueberblendung im Stilblatt, plus etwas Zugabe. */
const GLEIT_DAUER = 470;

/*
 * Dauer der Einfahrt von unten (Stilblatt, dp-kauffenster-ein), plus etwas
 * Zugabe. Solange sie laeuft, wird die Lage nicht beurteilt.
 */
const EINFAHRT_DAUER = 520;

let taucht = false;
let tauchZeit = 0;

/**
 * Laesst das Fenster auf einen neuen Abstand zum unteren Rand gleiten.
 *
 * Fuer den Weg zwischen beiden Plaetzen wird es kurz am Bildschirm
 * festgemacht - genau dort, wo es gerade steht. Von da aus laesst sich der
 * Abstand nach unten gleitend veraendern. Im Fluss verankert ginge das
 * nicht: dort haengt die Lage am Dokument, und ein Wechsel des
 * Bezugspunkts laesst sich nicht ueberblenden.
 *
 * @param {HTMLElement} kauf
 * @param {number} ziel Abstand der Unterkante zum unteren Bildschirmrand.
 */
function gleiteZu(kauf, ziel) {
  const istAbstand = Math.round(window.innerHeight - kauf.getBoundingClientRect().bottom);
  kauf.classList.remove('dp-kauffenster--geparkt');
  kauf.style.removeProperty('top');
  loeseSeiten(kauf);
  kauf.style.setProperty('inset-block-end', istAbstand + 'px');

  // Den Zwischenstand erzwingen, sonst fasst der Browser beide Schritte
  // zusammen und es gleitet nichts.
  void kauf.offsetHeight;

  gleitet = true;
  kauf.classList.add('dp-kauffenster--gleitet');
  kauf.style.setProperty('inset-block-end', ziel + 'px');

  window.clearTimeout(gleitZeit);
  gleitZeit = window.setTimeout(() => {
    gleitet = false;
    kauf.classList.remove('dp-kauffenster--gleitet');
    /*
     * Angekommen wird wieder im Fluss verankert - an derselben Stelle, an
     * der das Gleiten endet, also ohne sichtbaren Sprung. Hat sich die
     * Galerie waehrend der Fahrt noch einmal geaendert, faehrt es von hier
     * aus weiter, statt zu springen.
     */
    pruefeLageSpaeter();
  }, GLEIT_DAUER);
}

/*
 * Ab wann ein Unterschied ein Wechsel ist. Darunter ist es Rundung, und ein
 * Gleiten ueber zwei Pixel saehe niemand.
 */
const LAGE_SCHWELLE = 4;

/*
 * So lange muss die Galerie still sein, bevor ueber die Lage entschieden wird.
 *
 * Ein Farbwechsel aendert ihre Hoehe nicht in einem Zug: erst faellt sie auf
 * die Hoehe der neuen, noch leeren Fassung, dann waechst sie, wie ihre Bilder
 * ihre Masse melden. Wer jedem dieser Staende folgt, faehrt drei Wege
 * hintereinander. Abgewartet wird deshalb, bis sie steht - solange bleibt das
 * Fenster, wo es ist, und faehrt dann einen einzigen Weg.
 */
const RUHE_LAGE = 180;

let lageWecker = 0;

function pruefeLageSpaeter() {
  window.clearTimeout(lageWecker);
  lageWecker = window.setTimeout(pruefeLageWeich, RUHE_LAGE);
}

/**
 * Prueft, ob das Fenster noch am richtigen Platz steht - und gleitet dorthin,
 * wenn nicht.
 *
 * Gedacht fuer alles, was die Hoehe der Bilder aendert: der Wechsel der Farbe
 * tauscht die ganze Galerie aus, und mit ihr aendert sich die Kante, an der
 * das Fenster andockt. Frueher blieb es stehen, wo es war - bei einer kuerzer
 * gewordenen Galerie tief unten oder gar unter dem Bildschirmrand - und fand
 * seinen Platz erst beim naechsten Scrollen.
 *
 * Der Weg dorthin wird gefahren und nicht gesprungen; steht das Fenster schon
 * richtig, bleibt es unberuehrt.
 */
function pruefeLageWeich() {
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf) return;

  if (BREIT.matches) {
    haltAmBildende();
    return;
  }

  // Waehrend eines Gleitens nicht dazwischenfahren - danach noch einmal sehen.
  if (gleitet || taucht) {
    pruefeLageSpaeter();
    return;
  }

  /*
   * Laeuft gerade ein Galerie-Austausch, steht die Hoehe der Bilder noch
   * nicht fest: zwischen der alten und der neuen Fassung liegt ein Moment,
   * in dem die Galerie fast leer ist. Wer da schon entscheidet, faehrt
   * einen Weg, den er gleich darauf wieder zurueckfahren muss.
   */
  if (document.documentElement.dataset.dpGalerieWechselt !== undefined) {
    pruefeLageSpaeter();
    return;
  }

  /*
   * Solange das Fenster noch nicht zu sehen ist, wird es gestellt und nicht
   * gefahren - ein Weg, den niemand sieht, waere nur verlorene Zeit.
   */
  if (!kauf.classList.contains('dp-kauffenster--bereit')) {
    haltAmBildende();
    return;
  }

  /*
   * Bei offener Grossansicht gehoert das Fenster an den unteren Rand - das
   * entscheidet der eigene Weg, nicht die Hoehe der Bilder.
   */
  if (document.documentElement.dataset.dpBildfeld === 'offen') {
    haltAmBildende();
    return;
  }

  /*
   * Im Leisten-Modus entscheidet lageMinimiert - hier mit weichen Wegen:
   * dieser Pfad laeuft, wenn sich die Bilder geaendert haben (Farbwechsel,
   * nachgezogene Kacheln), nicht beim Rollen. Schrumpft die Galerie und
   * liegt ihr Ende ploetzlich ueber der Leiste am Rand, gleitet die Leiste
   * an die Bildkante, statt dorthin zu springen.
   */
  if (angedockt) {
    const galerie = holeGalerie();
    const spalte = document.querySelector('.product-details');
    if (galerie && spalte) lageMinimiert(kauf, galerie, spalte, true);
    else haltAmBildende();
    return;
  }

  const ist = Math.round(window.innerHeight - kauf.getBoundingClientRect().bottom);
  const soll = ruheAbstand(kauf);

  if (Math.abs(soll - ist) < LAGE_SCHWELLE) {
    haltAmBildende();
    return;
  }

  gleiteZu(kauf, soll);
}

/*
 * Haelt das Fenster waehrend eines Galerie-Austauschs am Bildschirm fest.
 *
 * Beim Farbwechsel baut sich die Bilderflaeche darueber neu auf: sie faellt
 * erst in sich zusammen und waechst dann, wie ihre Bilder ihre Masse melden.
 * Im Fluss verankert wanderte das Fenster diesen ganzen Weg mit - mal weit
 * unter den Bildschirmrand, mal darueber hinaus. Fuer die Dauer des
 * Austauschs wird es deshalb am Bildschirm festgemacht, genau dort, wo es
 * gerade steht; danach sucht es sich seinen neuen Platz und gleitet einmal
 * dorthin.
 *
 * Den Merkzettel setzt dp-galeriewechsel.js.
 */
function beobachteGalerieWechsel() {
  const wurzel = document.documentElement;

  const waechter = new MutationObserver(() => {
    const kauf = document.querySelector('.dp-kauffenster');
    if (!kauf || BREIT.matches) return;

    if (wurzel.dataset.dpGalerieWechselt === undefined) {
      // Vorbei - jetzt den Weg zum neuen Platz suchen.
      pruefeLageSpaeter();
      return;
    }

    if (gleitet) return;

    /*
     * Mindestens die gewohnte Luft nach unten: stand das Fenster im Moment
     * des Wechsels schon halb unter dem Rand, kommt es dabei gleich wieder
     * ganz ins Bild.
     */
    const abstand = Math.max(
      luftUnten(),
      Math.round(window.innerHeight - kauf.getBoundingClientRect().bottom)
    );
    kauf.classList.remove('dp-kauffenster--geparkt');
    kauf.style.removeProperty('top');
    loeseSeiten(kauf);
    kauf.style.setProperty('inset-block-end', abstand + 'px');
  });

  waechter.observe(wurzel, {
    attributes: true,
    attributeFilter: ['data-dp-galerie-wechselt'],
  });
}

function beobachteGrossansicht() {
  const waechter = new MutationObserver(() => {
    const kauf = document.querySelector('.dp-kauffenster');
    if (!kauf || BREIT.matches) return;

    /*
     * Offen: das ganze Fenster kommt an den unteren Rand - eine Leiste
     * faehrt dafuer erst auf. Zu: es geht dorthin zurueck, wo es hingehoert;
     * im Leisten-Modus faehrt es wieder zusammen, sofern es nicht unter die
     * Bilder passt.
     */
    const offen = document.documentElement.dataset.dpBildfeld === 'offen';
    if (offen) {
      if (istKlein(kauf)) fahreAuf(kauf, luftUnten(), haltAmBildende);
      else gleiteZu(kauf, luftUnten());
      /*
       * Die Modus-Anzeige im Knopf: sofort mit den Massen, die schon
       * stehen, und noch einmal, wenn Fahrt und Rahmen zur Ruhe gekommen
       * sind.
       */
      pflegeModusZeichen();
      // Ein paar Blicke hinterher: Rahmen und Fenster brauchen je nach Geraet unterschiedlich lang.
      for (const wann of [120, GLEIT_DAUER + 60, 900, 1500]) window.setTimeout(pflegeModusZeichen, wann);
      return;
    }

    const galerie = holeGalerie();
    if (angedockt && galerie && !passtUnterDieBilder(kauf, galerie.getBoundingClientRect().bottom, 0)) {
      fahreZusammen(kauf);
      return;
    }
    gleiteZu(kauf, ruheAbstand(kauf));
  });

  waechter.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-dp-bildfeld'],
  });
}

/**
 * Fasst alles oberhalb der Optionsauswahl in einen eigenen Bereich.
 *
 * Nur dieser Bereich scrollt, wenn der Inhalt zu hoch wird. Farbauswahl,
 * Mengenzaehler und Warenkorb-Knopf stehen ausserhalb davon und bleiben damit
 * von selbst am unteren Rand stehen - ohne zu haften, ohne eigene Flaeche und
 * ohne dass etwas hinter ihnen durchlaufen kann.
 *
 * Die Bloecke im Kauffenster stellt der Theme-Editor zusammen; ein Kasten
 * darum laesst sich dort nicht anlegen, deshalb entsteht er hier.
 */
/*
 * Am Handy rollt ein Wischen auf dem schwebenden Kauffenster nie die Seite
 * dahinter (Jimmi, Oktober 2026): Auf Stellen, die selbst nicht rollen (Kopf,
 * Kaufzeile), und am Ende eines Rollbereichs reichte der Browser die Geste an
 * die Seite weiter - waehrend des Ladens (dp-kauffenster--gesperrt, alles
 * inert) ueberall. Jetzt darf nur ein Bereich im Fenster die Geste nehmen,
 * der in diese Richtung noch rollen kann; sonst wird sie abgesagt.
 * Angedockt (dp-kauffenster--geparkt) steht das Fenster im Fluss der Seite -
 * dort rollt die Seite wie gewohnt, nur waehrend des Ladens nicht. Der Griff zum Ziehen (dp-reiter.js) sagt
 * seine Geste ohnehin selbst ab. Zwei Finger (Zoomen) bleiben frei.
 */
const NUR_HANDY = window.matchMedia('(max-width: 749px)');

/** @param {Element|null} el @param {Element} grenze @param {number} dx @param {number} dy */
function kannRollen(el, grenze, dx, dy) {
  const senkrecht = Math.abs(dy) >= Math.abs(dx);
  for (let e = el; e && e !== grenze.parentElement; e = e.parentElement) {
    if (!(e instanceof HTMLElement)) continue;
    const stil = getComputedStyle(e);
    if (senkrecht) {
      if (!/(auto|scroll)/.test(stil.overflowY) || e.scrollHeight <= e.clientHeight + 1) continue;
      // Finger nach unten (dy > 0) rollt nach oben.
      if (dy > 0 ? e.scrollTop > 0 : e.scrollTop + e.clientHeight < e.scrollHeight - 1) return true;
    } else {
      if (!/(auto|scroll)/.test(stil.overflowX) || e.scrollWidth <= e.clientWidth + 1) continue;
      const links = Math.abs(e.scrollLeft);
      if (dx > 0 ? links > 0 : links + e.clientWidth < e.scrollWidth - 1) return true;
    }
  }
  return false;
}

function haelteWischen(kauf) {
  if (kauf.dpWischHalt) return;
  kauf.dpWischHalt = true;
  let start = null;
  kauf.addEventListener(
    'touchstart',
    (ereignis) => {
      start = ereignis.touches.length === 1 ? { x: ereignis.touches[0].clientX, y: ereignis.touches[0].clientY } : null;
    },
    { passive: true }
  );
  kauf.addEventListener(
    'touchmove',
    (ereignis) => {
      if (!start || ereignis.touches.length !== 1 || !ereignis.cancelable) return;
      if (!NUR_HANDY.matches) return;
      const gesperrt = kauf.classList.contains('dp-kauffenster--gesperrt');
      if (!gesperrt && kauf.classList.contains('dp-kauffenster--geparkt')) return;
      // Ein Schieberegler zieht selbst - ihm die Geste abzusagen, naehme ihm den Zug.
      if (ereignis.target instanceof HTMLInputElement && ereignis.target.type === 'range') return;
      const finger = ereignis.touches[0];
      const dx = finger.clientX - start.x;
      const dy = finger.clientY - start.y;
      if (!dx && !dy) return;
      if (gesperrt || !kannRollen(/** @type {Element} */ (ereignis.target), kauf, dx, dy)) ereignis.preventDefault();
    },
    { passive: false }
  );
}

function fasseLaufBereich() {
  const kauf = document.querySelector('.dp-kauffenster');
  if (kauf instanceof HTMLElement) haelteWischen(kauf);
  const inhalt = kauf?.querySelector('.group-block-content');

  /*
   * Der Bereich reicht bis zur Optionsauswahl - und wo es keine gibt, bis zur
   * Kaufzeile.
   *
   * Artikel ohne Varianten haben keine Optionsauswahl. Fehlte der Bezugspunkt,
   * entstand gar kein Scrollbereich: das Fenster schnitt seinen Inhalt dann
   * unten ab, und die Wischgeste bewegte die Seite dahinter statt des Fensters.
   */
  const auswahl =
    inhalt?.querySelector(':scope > variant-picker') ||
    inhalt?.querySelector(':scope > .buy-buttons-block');

  if (!inhalt || !auswahl) return;

  let lauf = inhalt.querySelector(':scope > .dp-kauffenster__lauf');
  if (!lauf) {
    lauf = document.createElement('div');
    lauf.className = 'dp-kauffenster__lauf';
    inhalt.insertBefore(lauf, inhalt.firstChild);
  }

  // Alles zwischen dem Bereich und der Optionsauswahl wandert hinein.
  let kind = lauf.nextSibling;
  let umgehaengt = false;
  while (kind && kind !== auswahl) {
    const naechstes = kind.nextSibling;

    /*
     * Der Pfeil-Anzeiger bleibt draussen: er wird weiter unten ausdruecklich
     * hinter den Bereich gestellt. Wanderte er hier hinein, entstuende ein
     * Kreisel - jedes Umhaengen meldet der Beobachter des Aufbaus, der diese
     * Funktion erneut anstiesse, vierzigmal in der Sekunde, ohne Ende.
     */
    if (kind instanceof Element && kind.classList.contains('dp-kauffenster__mehr')) {
      kind = naechstes;
      continue;
    }

    lauf.appendChild(kind);
    kind = naechstes;
    umgehaengt = true;
  }

  /*
   * Beschreibung und Personalisierung kommen zusammen in einen Kasten.
   *
   * Am Rechner sollen die beiden Texte gemeinsam rollen und nicht jeder fuer
   * sich - zwei getrennte Rollbereiche untereinander liest niemand gern. Ein
   * gemeinsamer Kasten laesst sich mit CSS allein nicht bilden, die beiden sind
   * Geschwister.
   *
   * Auf dem Telefon ist der Kasten unsichtbar: das Stylesheet setzt ihn dort
   * auf display: contents, er faellt also aus dem Aufbau heraus und die
   * Reiterlogik findet alles wie zuvor.
   */
  const texte = [...lauf.children].filter((k) => k.matches(".accordion, accordion-custom"));

  if (texte.length > 0 && !lauf.querySelector(":scope > .dp-kauffenster__texte")) {
    const kasten = document.createElement("div");
    kasten.className = "dp-kauffenster__texte";
    texte[0].before(kasten);
    for (const teil of texte) kasten.appendChild(teil);
  }

  /*
   * Ohne Bildlaufleiste ist nicht zu sehen, dass es weitergeht. Ein kleiner
   * Pfeil an der unteren Kante des Bereichs sagt es.
   *
   * Er wird erst nach dem Umhaengen gesetzt und danach ausdruecklich hinter den
   * Bereich gestellt: stuende er vorher da, saugte ihn die Schleife oben mit
   * hinein, und er wanderte beim Scrollen mit dem Inhalt mit.
   */
  let anzeiger = kauf.querySelector('.dp-kauffenster__mehr');
  if (!anzeiger) {
    anzeiger = document.createElement('button');
    anzeiger.type = 'button';
    anzeiger.className = 'dp-kauffenster__mehr';
    anzeiger.setAttribute('aria-label', 'Weiter nach unten');
    anzeiger.innerHTML =
      '<svg viewBox="0 0 10 6" width="11" height="7" fill="none" aria-hidden="true">' +
      '<path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.6" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';

    /*
     * Ein Klick bringt eine Bildschirmlaenge weiter - abzueglich eines
     * Streifens, damit die letzte gelesene Zeile noch zu sehen bleibt und der
     * Anschluss nicht verlorengeht.
     */
    anzeiger.addEventListener('click', () => {
      const bereich = rollbereich();
      if (!bereich) return;

      bereich.scrollBy({ top: Math.max(80, bereich.clientHeight - 48), behavior: 'smooth' });
    });
  }

  /*
   * Der Horcher haengt am Bereich, nicht am Anzeiger: nach einem
   * Variantenwechsel entsteht der Bereich neu, der Anzeiger bleibt. Das
   * Merkmal am Element verhindert, dass sich die Horcher stapeln.
   */
  if (lauf.dataset.dpHorcht === undefined) {
    lauf.dataset.dpHorcht = '';
    lauf.addEventListener('scroll', pruefeAnzeiger, { passive: true });
  }

  /*
   * Gerollt wird auf dem Handy im geoeffneten Text - dort muss der Pfeil also
   * ebenfalls zuhoeren. Der Horcher haengt in der Einfangphase am Fenster:
   * Rollereignisse steigen nicht auf, und welcher Text gerade offen ist,
   * wechselt.
   */
  if (kauf.dataset.dpHorchtText === undefined) {
    kauf.dataset.dpHorchtText = '';
    kauf.addEventListener('scroll', pruefeAnzeiger, { passive: true, capture: true });
    document.addEventListener('toggle', pruefeAnzeiger, true);
  }

  /*
   * Nur umhaengen, wenn er nicht schon dort steht: das Einsetzen an derselben
   * Stelle zaehlt fuer den Beobachter des Aufbaus trotzdem als Umbau und
   * stiesse diese Funktion in einer Schleife immer wieder an.
   */
  if (lauf.nextSibling !== anzeiger) lauf.after(anzeiger);
  pruefeAnzeiger();

  /*
   * Ein Element umzuhaengen meldet es vom Dokument ab und wieder an - der
   * Ziehharmonika-Baustein des Themes setzt dabei seinen Ausgangszustand neu,
   * und die Beschreibung klappte wieder zu. Am Rechner ist ihre Kopfzeile
   * ausgeblendet, sie waere damit gar nicht mehr zu oeffnen; deshalb hier
   * wieder aufziehen.
   */
  if (umgehaengt && BREIT.matches) richteBeschreibung();
}

/**
 * Der Bereich, in dem tatsaechlich gerollt wird.
 *
 * Auf dem Handy ist das seit dem Umbau nicht mehr die ganze Spalte, sondern
 * der geoeffnete Text: Name, Preis und die Reiterzeile stehen fest, nur die
 * Beschreibung oder die Personalisierung rollt. Steht keiner der beiden offen,
 * bleibt es beim alten Bereich.
 *
 * @returns {HTMLElement | null}
 */
function rollbereich() {
  /*
   * Wo tatsaechlich gerollt wird, haengt an der Ansicht: am Rechner im
   * gemeinsamen Kasten um Beschreibung und Personalisierung, auf dem Telefon
   * im geoeffneten Text, und wenn keines von beidem greift, im alten Bereich.
   *
   * Entschieden wird nicht nach Bildschirmbreite, sondern danach, welcher der
   * Kaesten wirklich ein Rollbereich ist - das steht in seinem Stil. Auf dem
   * Telefon faellt der gemeinsame Kasten ueber display: contents aus dem Aufbau
   * heraus und rollt nicht, am Rechner tut er es.
   */
  const kandidaten = [
    /*
     * Im Transferdruck-Fenster liegen Beschreibung und Konfiguration in
     * einem gemeinsamen Rollbereich eine Ebene hoeher - der Textkasten
     * rollt dort nicht mehr selbst. Der Rollbereich ist der Laufbereich, nicht
     * der Kasten darum: sonst rollte die Kaufzeile mit. Er steht vor dem
     * Textkasten, dessen overflow-y neben overflow-x: hidden zu auto wird,
     * ohne dass er sich je bewegt.
     */
    document.querySelector('.dp-kauffenster--transfer > .group-block-content > .dp-kauffenster__lauf'),
    document.querySelector('.dp-kauffenster__texte'),
    document.querySelector('.dp-kauffenster details[open] .details-content'),
    document.querySelector('.dp-kauffenster__lauf'),
  ];

  for (const kasten of kandidaten) {
    if (!kasten) continue;

    /*
     * Ein Kasten ohne eigene Flaeche rollt nicht, was auch immer sein Stil
     * sagt. Auf dem Telefon traegt der gemeinsame Kasten overflow-x: hidden
     * und faellt ueber display: contents aus dem Aufbau - und nach CSS-Regel
     * wird aus seinem overflow-y dann ein auto. Ohne diese Pruefung hielt
     * das Skript ihn fuer den Rollbereich, und alles, was daran haengt (die
     * weichen Kanten, der Pfeil), sass an einem Kasten, der sich nie bewegt.
     */
    const stil = getComputedStyle(kasten);
    if (stil.display === 'contents' || stil.display === 'none') continue;

    const ueberlauf = stil.overflowY;
    if (ueberlauf === 'auto' || ueberlauf === 'scroll') return kasten;
  }

  return null;
}

/**
 * Zeigt oder verbirgt den Pfeil, je nachdem ob unter dem Sichtbaren noch etwas
 * liegt.
 *
 * Am Ende des Weges angekommen verschwindet er wieder - dann gibt es nichts
 * mehr anzukuendigen.
 */
function pruefeAnzeiger() {
  const kauf = document.querySelector('.dp-kauffenster');
  const bereich = rollbereich();
  if (!kauf || !bereich) {
    pflegeKanten(null, 0);
    return;
  }

  const rest = bereich.scrollHeight - bereich.scrollTop - bereich.clientHeight;
  kauf.classList.toggle('dp-kauffenster--mehr', rest > 4);
  pflegeKanten(bereich, rest);
}

/*
 * Die weichen Kanten des Rollbereichs.
 *
 * Oben laeuft der Text aus, sobald darueber schon etwas weggerollt ist, unten,
 * solange darunter noch etwas folgt - wie an den Farbpunkten, nur senkrecht.
 * Die Maske steht im Stilblatt (dp-rollkante), hier wird nur entschieden, wie
 * breit jede Kante gerade ist.
 *
 * Gefuehrt per Web Animations und nicht per transition: Die Rollkaesten tragen
 * eigene Uebergaenge fuer das Auf- und Zuklappen der Reiter, und eine
 * transition fuer die Kanten ersetzte diese Liste. Eine Animation laeuft
 * daneben her, ohne etwas zu verdraengen.
 *
 * Angefasst wird nur, wenn sich eine Kante wirklich aendert - beim Scrollen
 * ist das zweimal, nicht bei jedem Schritt.
 */
const KANTE = 28;
const KANTE_DAUER = 320;
const KANTE_TAKT = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
const KANTEN = new WeakMap();
let letzterRollkasten = null;

function pflegeKanten(bereich, rest) {
  if (letzterRollkasten && letzterRollkasten !== bereich) {
    stelleKante(letzterRollkasten, '--dp-fade-oben', 0, true);
    stelleKante(letzterRollkasten, '--dp-fade-unten', 0, true);
    letzterRollkasten.classList.remove('dp-rollkante');
  }
  letzterRollkasten = bereich;
  if (!bereich) return;

  bereich.classList.add('dp-rollkante');
  stelleKante(bereich, '--dp-fade-oben', bereich.scrollTop > 2 ? KANTE : 0);
  stelleKante(bereich, '--dp-fade-unten', rest > 2 ? KANTE : 0);
}

function stelleKante(kasten, name, ziel, sofort = false) {
  const stand = KANTEN.get(kasten) || {};
  if (stand[name] === ziel) return;
  stand[name] = ziel;
  KANTEN.set(kasten, stand);

  /* Laeuft noch eine Blende, geht die neue von dort aus weiter, wo sie gerade steht. */
  const vorher = parseFloat(getComputedStyle(kasten).getPropertyValue(name)) || 0;
  const lauf = stand[name + ':lauf'];
  if (lauf) lauf.cancel();

  kasten.style.setProperty(name, ziel + 'px');
  if (vorher === ziel) return;
  if (sofort || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  try {
    stand[name + ':lauf'] = kasten.animate(
      { [name]: [vorher + 'px', ziel + 'px'] },
      { duration: KANTE_DAUER, easing: KANTE_TAKT }
    );
  } catch (fehler) {
    /* Ohne Animation fuer eigene Groessen steht die Kante eben gleich richtig. */
  }
}

/*
 * Nach einem Variantenwechsel zeichnet das Theme die Spalte neu - der Bereich
 * ist dann wieder aufgeloest und wird hier neu gefasst.
 */
function beobachteAufbau() {
  const kauf = document.querySelector('.dp-kauffenster');
  const inhalt = kauf?.querySelector('.group-block-content');
  if (!inhalt) return;

  let geplant = false;
  const waechter = new MutationObserver(() => {
    if (geplant) return;
    geplant = true;
    window.setTimeout(() => {
      geplant = false;
      fasseLaufBereich();
    }, 0);
  });

  waechter.observe(inhalt, { childList: true });
}

/*
 * Ein Reiter im Kauffenster geht nur auf Zutun zu.
 *
 * Der Ziehharmonika-Baustein des Themes setzt beim Einhaengen ins Dokument
 * seinen Ausgangszustand neu - auf dem Handy heisst das: zu. Wird irgendwo im
 * Fenster etwas umgehaengt, faellt ein offener Reiter also von selbst
 * zusammen. Am Rechner wurde er danach wieder aufgezogen (richteBeschreibung),
 * auf dem Handy nicht.
 *
 * Das war der Grund, aus dem unter Android die Tastatur verschwand, sobald
 * aus einem Eingabefeld heraus ein Bild geoeffnet wurde: Der Reiter mit dem
 * Feld ging zu, der Browser uebersprang seinen Inhalt, das Feld verlor den
 * Schreibpunkt - und Android nahm daraufhin die Tastatur weg. Im Aufbau war
 * davon nichts zu sehen, weil ein zugeklapptes <details> seinen Inhalt an
 * einem Pseudo-Element versteckt und der Wechsel weich gefahren wird: Die
 * Wirkung kam erst eine halbe Sekunde nach der Ursache.
 *
 * Jedes Zugehen wird deshalb daraufhin angesehen, ob ein Griff dahinterstand -
 * ein Tippen auf die Kopfzeile. Kam es nicht von dort, wird es zurueckgenommen,
 * noch bevor der Browser den Inhalt wirklich ueberspringt.
 */
/*
 * Was als Griff zaehlt: die Kopfzeile eines Bereichs und die Reiterknoepfe.
 *
 * Auf dem Handy steht ueber den beiden Texten keine <summary>, sondern eine
 * Reihe Knoepfe - ein Tippen darauf klappt den einen Reiter zu und den
 * anderen auf. Wurde nur auf <summary> gehorcht, galt genau dieses Tippen
 * nicht als Griff, und der Reiter ging sofort wieder auf.
 *
 * Gehorcht wird auf pointerdown und auf click: Ein Klick aus einem Skript
 * (etwa der Sprung zur Personalisierung) bringt kein pointerdown mit.
 */
const GRIFFE = '.dp-kauffenster summary, .dp-reiter__knopf';
let letzterGriff = 0;
for (const art of ['pointerdown', 'click']) {
  document.addEventListener(
    art,
    (ereignis) => {
      const ziel = ereignis.target;
      if (ziel instanceof Element && ziel.closest(GRIFFE)) letzterGriff = Date.now();
    },
    true
  );
}

function halteReiterOffen() {
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf || kauf.dataset.dpHaeltReiter !== undefined) return;
  kauf.dataset.dpHaeltReiter = '';

  new MutationObserver((eintraege) => {
    for (const eintrag of eintraege) {
      const bereich = eintrag.target;
      if (!(bereich instanceof HTMLDetailsElement) || bereich.open) continue;
      /* Nie vom Kunden geoeffnet? Dann ist "zu" der gewollte Ausgangszustand. */
      if (bereich.dataset.dpVomNutzer === undefined) continue;
      /*
       * Und nur, solange ueber der Tastatur eine Zeile steht.
       *
       * Geht die Grossansicht auf, klappen die Reiter mit Absicht zu - das
       * Bild bekommt dadurch den Platz. Nur wenn gerade jemand schreibt, darf
       * das nicht passieren: Der Reiter traegt dann das Feld, sein Inhalt
       * wird uebersprungen, das Feld verliert den Schreibpunkt und Android
       * nimmt die Tastatur weg.
       */
      if (!document.querySelector('.dp-veredelung__feld--gehoben')) continue;
      /* Ein Griff an der Kopfzeile - auch der Wechsel auf den anderen Reiter zaehlt dazu. */
      if (Date.now() - letzterGriff < 600) continue;
      bereich.open = true;
    }
  }).observe(kauf, { attributes: true, subtree: true, attributeFilter: ['open'] });
}

function beobachteFenster() {
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf || !('ResizeObserver' in window)) return;

  const waechter = new ResizeObserver(() => {
    reserviere();
    haltAmBildende();
    pruefeAnzeiger();
    begrenzeAufGalerie();
  });

  waechter.observe(kauf);

  // Die Kaufzeile eigens: ihre Hoehe aendert sich, ohne dass das Fenster waechst.
  const zeile = kauf.querySelector('.buy-buttons-block');
  if (zeile) waechter.observe(zeile);
}

function haltAmBildende() {
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf) return;

  // Waehrend des Gleitens gehoert die Lage der Ueberblendung, nicht dem Scrollen.
  if (gleitet) return;

  /*
   * Waehrend eines Galerie-Austauschs steht das Fenster am Bildschirm fest
   * (beobachteGalerieWechsel) - dort wird es nicht angeruehrt, auch nicht
   * vom Scrollen.
   */
  if (document.documentElement.dataset.dpGalerieWechselt !== undefined) return;

  if (BREIT.matches) {
    // Am Rechner haelt die haftende Spalte von selbst am Ende des Rasters.
    kauf.classList.remove('dp-kauffenster--geparkt');
    kauf.style.removeProperty('inset-block-end');
    kauf.style.removeProperty('top');
    loeseSeiten(kauf);
    return;
  }

  /*
   * Ist die Grossansicht eines Bildes offen, gehoert das Fenster an den
   * unteren Bildschirmrand: Dort steht es vollstaendig vor Augen, waehrend
   * darueber das Bild gezeigt wird. Bei Produkten mit wenigen Bildern haette
   * es sonst irgendwo in der Mitte gestanden.
   */
  if (document.documentElement.dataset.dpBildfeld === 'offen') {
    schwebe(kauf);
    return;
  }

  const galerie = holeGalerie();
  const spalte = document.querySelector('.product-details');
  if (!galerie || !spalte) return;

  /*
   * Gerechnet wird auf die Oberkante des Fensters: sie soll unter der
   * Unterkante der Bilder zu liegen kommen. Die Hoehe wird jedes Mal neu
   * gelesen - sie aendert sich, sobald die Beschreibung auf- oder zugeklappt
   * wird.
   */
  const unterkante = galerie.getBoundingClientRect().bottom;
  const hoehe = kauf.getBoundingClientRect().height;
  const schwebeOben = window.innerHeight - luftUnten() - hoehe;

  // Im Leisten-Modus gibt es zwei Plaetze: die Leiste am Rand oder das Fenster unter den Bildern.
  if (angedockt) {
    lageMinimiert(kauf, galerie, spalte);
    return;
  }

  if (unterkante + LUFT_ZUM_BILD > schwebeOben) {
    schwebe(kauf);
    return;
  }

  parke(kauf, galerie, spalte);
}

/**
 * Verankert das Fenster im Fluss, mit der Oberkante unter der Bildkante.
 *
 * @param {HTMLElement} kauf
 * @param {Element} galerie
 * @param {HTMLElement} spalte
 */
function parke(kauf, galerie, spalte) {
  const unterkante = galerie.getBoundingClientRect().bottom;

  /*
   * Ab hier ist das Fenster an der Bildkante angekommen - und wird deshalb im
   * Fluss verankert statt am Bildschirm.
   *
   * Am Bildschirm festgemacht muesste seine Lage bei jedem Scrollschritt neu
   * gesetzt werden. Das laeuft dem Scrollen des Browsers immer eine Spur
   * hinterher, und genau das sah man als Zittern. Im Fluss verankert bewegt
   * der Browser es selbst mit der Seite - ohne Zutun und damit ruhig.
   *
   * Der Abstand von oben gilt innerhalb der Produktspalte, die direkt unter
   * der Galerie beginnt. Er aendert sich beim Scrollen nicht, muss also nur
   * bei Groessenaenderungen neu gerechnet werden.
   */
  const galerieUntenImDokument = unterkante + window.scrollY;
  const spalteObenImDokument = spalte.getBoundingClientRect().top + window.scrollY;
  const oben = Math.round(galerieUntenImDokument + LUFT_ZUM_BILD - spalteObenImDokument);

  kauf.style.setProperty('top', oben + 'px');
  kauf.style.removeProperty('inset-block-end');
  setzeSeiten(kauf, spalte);
  kauf.classList.add('dp-kauffenster--geparkt');
}

/**
 * Rechnet die seitlichen Abstaende auf den Bildschirm um.
 *
 * Am Bildschirm festgemacht zaehlen die 12 Pixel aus dem Stilblatt vom
 * Bildschirmrand. Im Fluss verankert zaehlen sie dagegen ab der Produktspalte -
 * und die ist um den Seitenrand schmaler. Das Fenster wuerde beim Parken also
 * sichtbar schmaler. Hier wird der Unterschied herausgerechnet, damit es in
 * beiden Zustaenden gleich breit bleibt.
 *
 * Die Werte haengen nur an der Fensterbreite, nicht am Scrollstand.
 *
 * @param {HTMLElement} kauf
 * @param {HTMLElement} spalte
 */
function setzeSeiten(kauf, spalte) {
  /*
   * Beide Seiten bekommen denselben Wert.
   *
   * Die Produktspalte steht mittig zwischen den Seitenraendern des Themes -
   * links und rechts also gleich weit innen. Rechnete man jede Seite einzeln
   * aus, muesste die Fensterbreite mitgemessen werden, und die ist auf vielen
   * Geraeten keine ganze Zahl: die beiden Seiten rundeten dann unterschiedlich
   * und das Fenster stand um ein Pixel schief. Gerundet wird hier gar nicht.
   */
  const versatz = LUFT_SEITE - spalte.getBoundingClientRect().left;
  kauf.style.setProperty('inset-inline-start', versatz + 'px');
  kauf.style.setProperty('inset-inline-end', versatz + 'px');
}

/**
 * Abstand der Fensterunterkante zum unteren Bildschirmrand an seinem Ruheplatz
 * - also unter der Bildkante, solange die dafuer weit genug oben steht.
 *
 * @param {HTMLElement} kauf
 * @returns {number}
 */
function ruheAbstand(kauf) {
  const galerie = holeGalerie();
  if (!galerie) return luftUnten();

  const unterkante = galerie.getBoundingClientRect().bottom;
  const hoehe = kauf.getBoundingClientRect().height;
  return Math.max(
    luftUnten(),
    Math.round(window.innerHeight - unterkante - LUFT_ZUM_BILD - hoehe)
  );
}

/**
 * Legt das Fenster an den unteren Bildschirmrand.
 *
 * @param {HTMLElement} kauf
 */
function schwebe(kauf) {
  kauf.classList.remove('dp-kauffenster--geparkt');
  kauf.style.removeProperty('top');
  loeseSeiten(kauf);
  kauf.style.setProperty('inset-block-end', luftUnten() + 'px');
}

/**
 * Nimmt die umgerechneten Seitenabstaende wieder zurueck.
 *
 * @param {HTMLElement} kauf
 */
function loeseSeiten(kauf) {
  kauf.style.removeProperty('inset-inline-start');
  kauf.style.removeProperty('inset-inline-end');
}

/** Wie lange nach dem Laden ein Wachsen noch begleitet wird. */
const WACHSTUM_FENSTER = 8000;

/** Dauer der Bewegung. */
const WACHSTUM_DAUER = 420;

/**
 * Laesst das Kauffenster in seine Hoehe hineinwachsen, statt sie zu springen.
 *
 * Am Rechner stehen Name, Preis und Kaufzeile sofort da; Beschreibung und
 * Personalisierung kommen erst danach - die Bloecke werden nach dem ersten
 * Zeichnen umgehaengt, und ihr Text trifft noch spaeter ein. Das Fenster schoss
 * dabei auf seine volle Groesse.
 *
 * Gemessen wird nach jeder Aenderung im Fenster, und wenn es gewachsen ist,
 * laeuft die Hoehe von der alten zur neuen. Bewegt wird ueber die Animations-
 * schnittstelle und nicht ueber eine Regel im Stylesheet: die Hoehe ergibt sich
 * hier aus dem Inhalt, und ein Uebergang braucht zwei feste Werte.
 *
 * Nur nach dem Laden und nur am Rechner: spaetere Aenderungen - ein Reiter, der
 * aufgeht - haben ihre eigene Bewegung, und auf dem Handy sitzt das Fenster
 * ohnehin am unteren Rand.
 *
 * @param {HTMLElement} fenster
 */
function begleiteWachstum(fenster) {
  if (fenster.dataset.dpWaechst !== undefined) return;
  fenster.dataset.dpWaechst = '';

  let letzte = Math.round(fenster.getBoundingClientRect().height);
  const bis = performance.now() + WACHSTUM_FENSTER;
  let laeuft = null;

  const pruefe = () => {
    if (!BREIT.matches) return;
    if (performance.now() > bis) return;

    /*
     * Laeuft schon eine Bewegung, wird sie nicht abgewartet, sondern neu
     * ausgerichtet: von der Hoehe, die gerade zu sehen ist, auf die neue.
     *
     * Frueher wurden Aenderungen waehrend einer Bewegung verworfen. Der
     * Inhalt kommt aber in Schueben - die Felder der Personalisierung
     * treffen nach und nach ein -, und am Ende der Bewegung schnappte das
     * Fenster auf den inzwischen viel groesseren Endwert. Genau dieser
     * Sprung war zu sehen.
     */
    const von = laeuft ? Math.round(fenster.getBoundingClientRect().height) : letzte;
    if (laeuft) {
      laeuft.cancel();
      laeuft = null;
    }

    /*
     * Erst nach dem Abbrechen messen: waehrend einer Hoehen-Bewegung
     * meldet das Fenster den Zwischenwert, nicht sein Ziel.
     */
    const jetzt = Math.round(fenster.getBoundingClientRect().height);
    letzte = jetzt;

    /*
     * In beide Richtungen: der Inhalt kann auch schrumpfen - ein Feld
     * faellt weg, ein Text wird kuerzer. Ein hartes Zusammenfallen sieht
     * so unruhig aus wie ein hartes Wachsen.
     */
    if (Math.abs(jetzt - von) < 4) return;

    laeuft = fenster.animate(
      [{ height: von + 'px' }, { height: jetzt + 'px' }],
      { duration: WACHSTUM_DAUER, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
    );

    const fertig = () => {
      laeuft = null;
      letzte = Math.round(fenster.getBoundingClientRect().height);
    };

    laeuft.addEventListener('finish', fertig);
    laeuft.addEventListener('cancel', fertig);
  };

  new MutationObserver(pruefe).observe(fenster, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  /*
   * Nicht jede Groessenaenderung ist eine Aenderung am Baum.
   *
   * Bei Artikeln, deren Personalisierung schon im Quelltext steht, kommt
   * spaeter nichts hinzu - der Inhalt bekommt nur seinen endgueltigen Platz,
   * wenn die Schrift geladen ist, ein Bild seine Masse meldet oder ein
   * Reiter seine Hoehe findet. Der Beobachter oben schwieg dazu, und das
   * Fenster sprang. Deshalb werden zusaetzlich die Masse der Bloecke darin
   * verfolgt; die Bewegung selbst laeuft auf dem Fenster und ruehrt sie
   * nicht an, es entsteht also keine Rueckkopplung.
   */
  if ('ResizeObserver' in window) {
    const masse = new ResizeObserver(pruefe);
    const haenge = () => {
      for (const kind of fenster.children) {
        // Gemessen wird der ganze Block samt Polsterung: waechst nur die,
        // aendert sich das Fenster trotzdem.
        if (kind instanceof HTMLElement) masse.observe(kind, { box: 'border-box' });
      }
    };
    haenge();
    new MutationObserver(haenge).observe(fenster, { childList: true });
  }

  window.addEventListener('load', pruefe);
}

/*
 * Auf dem Handy erscheint das Fenster erst, wenn die anfangs sichtbaren
 * Produktbilder geladen sind - vorher schoebe es sich vor eine Galerie, die
 * sich noch fuellt. Gewartet wird nur auf Fotos im ersten Sichtbereich. Der
 * Wecker und der Notausstieg im Stilblatt sorgen dafuer, dass das Fenster
 * nie dauerhaft unsichtbar bleibt.
 */
/*
 * Stand das Fenster in diesem Seitenaufruf schon einmal, muss es nie
 * wieder warten - siehe holeZurueckNachWechsel().
 */
let schonGezeigt = false;

/**
 * Die Galerie, die der Besucher tatsaechlich vor sich hat.
 *
 * Im Dokument koennen mehrere media-gallery-Elemente stehen: das Theme
 * rendert Fassungen fuer verschiedene Breiten, und beim Variantenwechsel
 * bleibt die abgeloeste Fassung einen Moment liegen - auf einem Geraet
 * waren es vier gleichzeitig. Wer auf die Bilder "aller Galerien" wartet,
 * wartet damit auch auf Bilder unsichtbarer Fassungen, die nie geladen
 * werden: Das Fenster blieb bis zum Wecker nach acht Sekunden verborgen.
 *
 * @returns {Element | null}
 */
function sichtbareGalerie() {
  const alle = [...document.querySelectorAll('media-gallery')];
  for (let i = alle.length - 1; i >= 0; i -= 1) {
    const kandidat = alle[i];
    if (!(kandidat instanceof HTMLElement)) continue;
    if (kandidat.offsetParent === null && getComputedStyle(kandidat).position !== 'fixed') continue;
    const rand = kandidat.getBoundingClientRect();
    if (rand.width && rand.height) return kandidat;
  }
  return alle[0] || null;
}

function zeigeNachBildern() {
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf) return;

  const bereit = () => {
    /*
     * Erst den Platz suchen, dann zeigen: das Fenster schwebt von unten
     * herein und soll dabei genau dorthin fahren, wo es bleibt.
     */
    reserviere();
    haltAmBildende();

    /*
     * Fuer die Dauer der Einfahrt wird die Lage nicht mehr beurteilt. Die
     * Einfahrt verschiebt das Fenster mit translate; eine Messung mittendrin
     * meldete die Lage der Bewegung und nicht die des Platzes - und das
     * Fenster faehrt einen zweiten Weg an, den es gar nicht braucht. Genau
     * das sah man als kurzes Absacken kurz vor dem Stillstand.
     */
    taucht = true;
    window.clearTimeout(tauchZeit);
    tauchZeit = window.setTimeout(() => {
      taucht = false;
    }, EINFAHRT_DAUER);

    kauf.classList.add('dp-kauffenster--bereit');
    schonGezeigt = true;
  };

  if (!window.matchMedia('(max-width: 749px)').matches) {
    bereit();
    return;
  }

  /*
   * Gewartet wird nur auf die Bilder der sichtbaren Galerie. Frueher stand
   * hier das ganze Dokument - mit mehreren Galerien im Dokument hing das
   * Fenster an Bildern, die niemand sieht und die deshalb nie laden.
   */
  const sichtbar = sichtbareGalerie();
  const bilder = [...(sichtbar || document).querySelectorAll('.media-gallery__grid img')].filter((bild) => {
    if (!(bild instanceof HTMLImageElement)) return false;
    /*
     * Die abseits geparkte Vorschau des Warenkorbs gehoert nicht zur
     * Bilderstrecke. Ihr Abzug laedt unter Umstaenden gar nicht, und das
     * Fenster wartete dann bis zum Notausstieg nach vier Sekunden.
     */
    if (bild.closest('.dp-korb-perso-vorschau')) return false;
    if (!bild.getAttribute('src')) return false;
    return bild.getBoundingClientRect().top < window.innerHeight * 1.2;
  });

  let offen = bilder.filter((bild) => !bild.complete).length;
  if (!offen) {
    bereit();
  } else {
    const einer = () => {
      offen -= 1;
      if (offen <= 0) bereit();
    };
    for (const bild of bilder) {
      if (bild.complete) continue;
      bild.addEventListener('load', einer, { once: true });
      bild.addEventListener('error', einer, { once: true });
    }
  }

  // Der Wecker.
  window.setTimeout(bereit, 4000);
}

/**
 * Holt das Fenster nach einem Variantenwechsel sofort zurueck.
 *
 * Beim Farb- oder Groessenwechsel zeichnet das Theme den Abschnitt neu.
 * Wird das Kauffenster dabei ersetzt, fehlt dem neuen Element die
 * Bereitschaftsmarke - und die Wartelogik laeuft nur einmal beim Aufbau
 * der Seite. Das Fenster stuende dann unsichtbar da, bis der Notausstieg
 * im Stilblatt es nach viereinhalb Sekunden freigibt.
 *
 * Beim ersten Aufbau ist das Warten richtig: die Galerie soll ihre Hoehe
 * gefunden haben, bevor das Fenster erscheint. Beim Wechsel ist es das
 * nicht mehr - die Masse sind laengst bekannt, und wer die Farbe wechselt,
 * hat das Fenster gerade noch gesehen.
 *
 * Nachgefasst wird ohne Beobachter: der Abschnitt kommt nicht zu einem
 * festen Zeitpunkt zurueck, deshalb ein paar kurze, begrenzte Blicke.
 */
function holeZurueckNachWechsel() {
  document.addEventListener(
    'change',
    (ereignis) => {
      const ziel = ereignis.target;
      if (!(ziel instanceof Element) || !ziel.closest('variant-picker')) return;
      if (!schonGezeigt) return;

      for (const wann of [60, 200, 500, 900, 1500, 2400]) {
        window.setTimeout(() => {
          const kauf = document.querySelector('.dp-kauffenster');
          if (kauf) kauf.classList.add('dp-kauffenster--bereit');
        }, wann);
      }
    },
    true
  );
}

/* ------------------------------------------ Zusammenfahren zur Kaufleiste */

/*
 * Auf dem Handy laesst sich das Kauffenster zu einer schmalen Leiste
 * zusammenfahren.
 *
 * Von Haus aus schwebt es am unteren Bildschirmrand und nimmt dort gut ein
 * Drittel der Hoehe ein. Wer die Bilder in Ruhe anschauen will, tippt auf den
 * Knopf oben rechts: das Fenster faehrt an Ort und Stelle zusammen, bis nur
 * noch eine Zeile bleibt - Name, Preis, Farben und der Kaufknopf. Rollt man
 * bis ans Ende der Bilder, faehrt die Leiste wieder zum ganzen Fenster auf
 * und setzt sich unter die Bildkante; rollt man zurueck, faehrt es wieder
 * zusammen. Der Knopf in der Leiste (oder im aufgefahrenen Fenster) beendet
 * das Ganze: das Fenster verhaelt sich dann wieder wie zuvor.
 *
 * Es ist immer dasselbe Element: die Leiste liegt im Fenster und blendet
 * ein, waehrend der Inhalt ausblendet und die Hoehe faehrt. Der Zustand lebt
 * nur fuer diese Seite.
 */

/** Ob der Leisten-Modus an ist (Knopf gedrueckt). */
let angedockt = false;

const ANGEDOCKT = 'dp-kauffenster--angedockt';

/** Das Fenster steht gerade als Leiste da. */
const KLEIN = 'dp-kauffenster--klein';

/** Hoehe und Lage fahren gerade (Uebergang im Stilblatt). */
const FAEHRT = 'dp-kauffenster--faehrt';

/** Hoehe der Leiste: 44 Pixel Knopf und zweimal 8 Pixel Polster. */
const LEISTE_HOEHE = 60;

/** Dauer der Fahrt im Stilblatt (320 ms) plus Zugabe. */
const FAHRT_DAUER = 380;

/**
 * Spielraum, bevor ein aufgefahrenes Fenster wieder zusammenfaehrt. Ohne ihn
 * wechselte es an der Kante bei jedem Rollschritt hin und her.
 */
const HYSTERESE = 24;

/** Die Bildschirmhoehe bei der letzten Entscheidung ueber die Leiste - siehe lageMinimiert. */
let randHoeheZuletzt = 0;

/** Zuletzt gemessene Hoehe des ganzen Fensters - als Leiste ist sie nicht messbar. */
let hoeheVoll = 0;

/**
 * Ob das Fenster zuletzt als Leiste stand. Gemerkt ausserhalb des Elements:
 * beim Farbwechsel kann das Theme das Fenster ersetzen, und das neue kennt
 * seine Klassen nicht - ruesteAus stellt den Zustand daraus wieder her.
 */
let leisteAktiv = false;

let fahrtZeit = 0;

/** Beschriftungen des Knopfs fuer die Vorlesehilfe. */
const ANDOCK_WORT = 'Kauffenster zur Leiste zusammenfahren';
const LOESE_WORT = 'Kauffenster wieder ganz zeigen';

/** @param {HTMLElement} kauf */
function istKlein(kauf) {
  return kauf.classList.contains(KLEIN);
}

/**
 * Die Hoehe des ganzen Fensters. Solange es ganz dasteht, wird gemessen und
 * gemerkt; als Leiste oder waehrend einer Fahrt gilt der gemerkte Wert.
 *
 * @param {HTMLElement} kauf
 * @returns {number}
 */
function natuerlicheHoehe(kauf, frisch = false) {
  if (!istKlein(kauf) && !kauf.classList.contains(FAEHRT)) {
    hoeheVoll = kauf.getBoundingClientRect().height;
  } else if (frisch && istKlein(kauf)) {
    /*
     * Vor einer Fahrt zum ganzen Fenster wird frisch gemessen: der Inhalt
     * ist als Leiste nur ausgeblendet, nicht weg, und kann sich seit der
     * letzten Messung geaendert haben (Reiter gebaut, Beschreibung auf).
     * Kurz auf auto gestellt, gelesen, zurueckgesetzt - alles vor dem
     * naechsten Zeichnen, zu sehen ist davon nichts.
     */
    const merk = kauf.style.height;
    kauf.style.setProperty('height', 'auto');
    hoeheVoll = kauf.getBoundingClientRect().height;
    kauf.style.setProperty('height', merk);
    /*
     * Noch einmal lesen, jetzt mit der alten Hoehe: Das Messen hat das Fenster
     * innerlich schon auf sein volles Mass gezeichnet, und genau das waere
     * sonst der Ausgangspunkt der naechsten Fahrt gewesen - das Fenster sprang
     * dann auf, statt aufzugleiten.
     */
    void kauf.offsetHeight;
  }
  return hoeheVoll || kauf.getBoundingClientRect().height;
}

/**
 * Ob das ganze Fenster unter der Bildkante noch ins Bild passt.
 *
 * @param {HTMLElement} kauf
 * @param {number} unterkante Unterkante der Bilder am Bildschirm.
 * @param {number} spielraum
 */
function passtUnterDieBilder(kauf, unterkante, spielraum) {
  return unterkante + LUFT_ZUM_BILD + natuerlicheHoehe(kauf) <= window.innerHeight - luftUnten() + spielraum;
}

/**
 * Meldet den Beginn einer Fahrt und raeumt nach ihr auf.
 *
 * @param {HTMLElement} kauf
 * @param {(() => void) | null} danach
 */
function beginneFahrt(kauf, danach) {
  gleitet = true;
  kauf.classList.add(FAEHRT);
  window.clearTimeout(fahrtZeit);
  fahrtZeit = window.setTimeout(() => {
    kauf.classList.remove(FAEHRT);
    gleitet = false;
    if (danach) danach();
    reserviere();
    pflegePin();
  }, FAHRT_DAUER);
}

/**
 * Faehrt das Fenster dort, wo es gerade steht, zur Leiste am unteren Rand
 * zusammen.
 *
 * @param {HTMLElement} kauf
 */
function fahreZusammen(kauf) {
  if (istKlein(kauf)) return;

  hoeheVoll = kauf.getBoundingClientRect().height;

  // Am Bildschirm festmachen, genau dort, wo es steht - von da aus faehrt es.
  const ist = Math.round(window.innerHeight - kauf.getBoundingClientRect().bottom);
  kauf.classList.remove('dp-kauffenster--geparkt');
  kauf.style.removeProperty('top');
  loeseSeiten(kauf);
  kauf.style.setProperty('inset-block-end', ist + 'px');
  kauf.style.setProperty('height', Math.round(hoeheVoll) + 'px');

  // Den Zwischenstand erzwingen, sonst fasst der Browser beide Schritte zusammen.
  void kauf.offsetHeight;

  fuelleLeiste();
  beginneFahrt(kauf, () => {
    // Liegt die Bildkante weiter oben, gleitet die Leiste jetzt dorthin.
    const galerie = holeGalerie();
    const spalte = document.querySelector('.product-details');
    if (angedockt && galerie && spalte) lageMinimiert(kauf, galerie, spalte, true);
  });
  const endeUnten = luftUnten();
  schwebeInhalt(kauf, true, () => {
    kauf.classList.add(KLEIN);
    kauf.style.setProperty('inset-block-end', endeUnten + 'px');
    kauf.style.setProperty('height', LEISTE_HOEHE + 'px');
  });
  kauf.classList.add(KLEIN);
  leisteAktiv = true;
  kauf.style.setProperty('inset-block-end', luftUnten() + 'px');
  kauf.style.setProperty('height', LEISTE_HOEHE + 'px');
  reserviere();
}

/**
 * Faehrt die Leiste zum ganzen Fenster auf.
 *
 * @param {HTMLElement} kauf
 * @param {number} abstand Abstand der Unterkante zum unteren Rand am Ende der Fahrt.
 * @param {() => void} danach
 */
function fahreAuf(kauf, abstand, danach) {
  if (!istKlein(kauf)) return;

  /*
   * Erst am Bildschirm festmachen, genau dort, wo die Leiste steht - sie
   * kann inzwischen unter den Bildern verankert sein (lageMinimiert). Von
   * da aus laesst sich der Abstand zum Rand fahren.
   */
  const ist = Math.round(window.innerHeight - kauf.getBoundingClientRect().bottom);
  kauf.classList.remove('dp-kauffenster--geparkt');
  kauf.style.removeProperty('top');
  loeseSeiten(kauf);
  kauf.style.setProperty('inset-block-end', ist + 'px');
  kauf.style.setProperty('height', LEISTE_HOEHE + 'px');
  void kauf.offsetHeight;

  const hoehe = Math.round(natuerlicheHoehe(kauf, true));
  beginneFahrt(kauf, () => {
    kauf.style.removeProperty('height');
    danach();
  });
  schwebeInhalt(kauf, false, () => {
    kauf.classList.remove(KLEIN);
    kauf.style.setProperty('inset-block-end', Math.max(luftUnten(), abstand) + 'px');
    kauf.style.setProperty('height', hoehe + 'px');
  });
  kauf.classList.remove(KLEIN);
  leisteAktiv = false;
  kauf.style.setProperty('inset-block-end', Math.max(luftUnten(), abstand) + 'px');
  kauf.style.setProperty('height', hoehe + 'px');
  reserviere();
}

/**
 * Entscheidet im Leisten-Modus, was das Fenster gerade sein soll.
 *
 * Passt es ganz unter die Bildkante, steht es dort im Fluss verankert; sonst
 * ist es die Leiste am unteren Rand. Der Wechsel wird gefahren.
 *
 * @param {HTMLElement} kauf
 * @param {Element} galerie
 * @param {HTMLElement} spalte
 */
function lageMinimiert(kauf, galerie, spalte, weich = false) {
  const unterkante = galerie.getBoundingClientRect().bottom;
  const rand = window.innerHeight - luftUnten();
  const geparkt = kauf.classList.contains('dp-kauffenster--geparkt');

  /*
   * Auch die Leiste dockt unter den Bildern an, sobald sie dort Platz hat.
   * Von da an rollt sie mit der Seite - und faehrt an Ort und Stelle zum
   * Fenster auf, wenn auch das ganze Fenster unter die Bilder passt.
   * Frueher fuhr sie dafuer am Bildschirm festgemacht auf und wurde erst
   * danach im Fluss verankert: waehrend der Fahrt rollte sie mit dem
   * Bildschirm mit und sprang am Ende an ihren Platz.
   */
  if (istKlein(kauf)) {
    /*
     * Spielraum nur, waehrend sich die Bildschirmhoehe aendert: Safari
     * aendert sie beim Rollen, wenn seine Leisten ein- und ausfahren. Ohne
     * Spielraum kippte die Leiste dann an der Grenze bei jedem Rollschritt
     * zwischen verankert und schwebend hin und her - das sah man als Zittern.
     *
     * Beim gewoehnlichen Rollen dagegen gilt die Grenze genau: Dort fallen
     * beide Plaetze zusammen, und der Wechsel ist unsichtbar. Mit dem
     * Spielraum rollte die verankerte Leiste beim Hochrollen erst 24 Punkte
     * unter ihren Platz ueber der Home-Pille und sprang dann hart hinauf.
     */
    const hoeheWechselt = randHoeheZuletzt > 0 && Math.abs(window.innerHeight - randHoeheZuletzt) > 1;
    randHoeheZuletzt = window.innerHeight;
    const spielraum = geparkt && hoeheWechselt ? HYSTERESE : 0;
    const leistePasst = unterkante + LUFT_ZUM_BILD + LEISTE_HOEHE <= rand + spielraum;

    if (!leistePasst) {
      // Am unteren Rand - die Bildkante liegt noch zu tief.
      if (geparkt) schwebe(kauf);
      else if (!kauf.style.insetBlockEnd) schwebe(kauf);
      return;
    }

    if (!geparkt && weich) {
      /*
       * Vom Rand an die Bildkante - nur nach dem Zusammenfahren am Rand, wenn
       * die Bildkante weiter oben liegt: dann gleitet die Leiste hin und wird
       * am Ende der Fahrt verankert. Beim Rollen dagegen wird sofort
       * verankert, wie beim ganzen Fenster: an der Grenze fallen beide
       * Plaetze zusammen. Eine Fahrt bei jedem Rollschritt liess die Leiste
       * dem Finger hinterherlaufen, statt fest an der Kante zu sitzen - und
       * solange sie fuhr, kam sie nie zum Auffahren.
       */
      const ist = Math.round(window.innerHeight - kauf.getBoundingClientRect().bottom);
      const soll = Math.round(window.innerHeight - (unterkante + LUFT_ZUM_BILD + LEISTE_HOEHE));
      if (Math.abs(soll - ist) >= LAGE_SCHWELLE) {
        gleiteZu(kauf, soll);
        return;
      }
    }

    parke(kauf, galerie, spalte);

    /*
     * Passt auch das ganze Fenster darunter, faehrt es an Ort und Stelle
     * auf: verankert an der Bildkante, die Oberkante bleibt dort, die
     * Unterkante faehrt nach unten. Verankert rollt es dabei mit der Seite -
     * und liegt so nie unterhalb des Bildendes, egal wie schnell gerollt
     * wird. Ein Auffahren am Bildschirmrand (ein frueherer Versuch) blieb
     * beim schnellen Rollen zum Seitenende am Rand haengen, waehrend die
     * Bilder laengst weiter oben waren.
     */
    if (passtUnterDieBilder(kauf, unterkante, 0)) fahreAmPlatz(kauf, false, haltAmBildende);
    return;
  }

  /*
   * Das ganze Fenster. Passt es unter die Bilder, steht es dort verankert.
   *
   * Passt es beim Hochrollen nicht mehr (mit Spielraum, damit es an der
   * Grenze nicht flattert), faehrt es an Ort und Stelle von unten zu: die
   * Oberkante bleibt an der Bildkante verankert, die Unterkante steigt, bis
   * nur die Leiste bleibt - und die rollt dann mit der Seite weiter, bis
   * auch sie unter den Bildern keinen Platz mehr hat (Leisten-Zweig oben).
   * Beim Runterrollen kommt dieser Fall nie: dort wird der Platz nur mehr.
   *
   * Schwebt das grosse Fenster noch am Rand (etwa gleich nach dem Oeffnen
   * der Grossansicht), faehrt es dort zusammen.
   */
  if (geparkt) {
    if (passtUnterDieBilder(kauf, unterkante, HYSTERESE)) {
      parke(kauf, galerie, spalte);
      return;
    }
    fahreAmPlatz(kauf, true, haltAmBildende);
    return;
  }

  // Schwebend gross am Rand.
  if (passtUnterDieBilder(kauf, unterkante, 0)) {
    /*
     * Die Bildkante ist da - verankern. Liegt sie deutlich ueber dem
     * Fenster (nach einem Auffahren bei schnellem Rollen), gleitet das
     * Fenster erst hin, statt zu springen; die Fahrt endet mit dem Verankern
     * (pruefeLageWeich).
     */
    /*
     * Gleiten nur einmal, direkt nach dem Auffahren (weich). Beim Rollen wird
     * schlicht verankert, wie im gewohnten Modus - eine Fahrt bei jedem
     * Rollschritt liess das Fenster leicht auf und ab schweben, statt fest an
     * der Seite zu kleben.
     */
    if (weich) {
      const ist = Math.round(window.innerHeight - kauf.getBoundingClientRect().bottom);
      const soll = Math.round(window.innerHeight - (unterkante + LUFT_ZUM_BILD + natuerlicheHoehe(kauf)));
      if (Math.abs(soll - ist) >= LAGE_SCHWELLE) {
        gleiteZu(kauf, soll);
        return;
      }
    }
    parke(kauf, galerie, spalte);
    return;
  }

  if (!passtUnterDieBilder(kauf, unterkante, HYSTERESE)) fahreZusammen(kauf);
}

/**
 * Faehrt das Fenster an Ort und Stelle zwischen Leiste und ganzer Groesse -
 * die Lage bleibt, nur die Hoehe laeuft. Gedacht fuer das im Fluss verankerte
 * Fenster unter den Bildern: dort darf der Bezugspunkt nicht wechseln.
 *
 * @param {HTMLElement} kauf
 * @param {boolean} zurLeiste
 * @param {() => void} danach
 */
function fahreAmPlatz(kauf, zurLeiste, danach) {
  if (zurLeiste === istKlein(kauf)) return;

  const von = Math.round(kauf.getBoundingClientRect().height);
  const nach = zurLeiste ? LEISTE_HOEHE : Math.round(natuerlicheHoehe(kauf, true));
  if (zurLeiste) hoeheVoll = von;

  kauf.style.setProperty('height', von + 'px');
  void kauf.offsetHeight;

  if (zurLeiste) fuelleLeiste();
  beginneFahrt(kauf, () => {
    if (!zurLeiste) kauf.style.removeProperty('height');
    danach();
  });
  schwebeInhalt(kauf, zurLeiste, () => {
    kauf.classList.toggle(KLEIN, zurLeiste);
    kauf.style.setProperty('height', nach + 'px');
  });
  kauf.classList.toggle(KLEIN, zurLeiste);
  leisteAktiv = zurLeiste;
  kauf.style.setProperty('height', nach + 'px');
  // Der Platz unter den Bildern faehrt mit - siehe reserviere.
  reserviere();
}

/**
 * Der Knopf oben rechts im Fenster.
 *
 * Dieselbe runde Flaeche wie das Kreuz in den uebrigen Fenstern - nur mit
 * einem anderen Zeichen, denn geschlossen wird hier nichts.
 *
 * @param {HTMLElement} kauf
 */
function baueAndockKnopf(kauf) {
  if (kauf.querySelector('.dp-kauffenster__zu')) return;

  const knopf = document.createElement('button');
  knopf.type = 'button';
  knopf.className = 'dp-kauffenster__zu';
  knopf.setAttribute('aria-label', angedockt ? LOESE_WORT : ANDOCK_WORT);
  knopf.setAttribute('aria-pressed', angedockt ? 'true' : 'false');
  knopf.appendChild(anheftZeichen());

  knopf.addEventListener('click', () => {
    /* Steht Beschreibung oder Personalisierung offen, schliesst der Knopf zuerst sie. */
    if (!angedockt && schliesseOffenesFeld(kauf)) return;
    schalteAndocken(!angedockt);
  });
  kauf.prepend(knopf);
}

/**
 * Schliesst Beschreibung oder Personalisierung, falls eines davon offen steht.
 *
 * Der Knopf oben rechts hat damit zwei Aufgaben, wie man sie von einem
 * Zurueck erwartet: erst das Offene zu, dann das Fenster zur Leiste.
 * Gefragt wird der Reiter selbst (dp-reiter.js), und geschlossen wird mit
 * seinem eigenen Tipp - Zufahren und Pfeil laufen darueber wie gewohnt.
 *
 * Nicht ueber die offenen <details> im Fenster: Dort stehen je nach Artikel
 * noch andere offen, die zu keinem Reiter gehoeren. Frueher zaehlten die mit,
 * der Knopf hielt das Fenster fuer "Feld offen" und fuhr es nie mehr zusammen.
 *
 * @param {HTMLElement} kauf
 * @returns {boolean} Ob etwas geschlossen wurde.
 */
function schliesseOffenesFeld(kauf) {
  const reiter = kauf.querySelector('.dp-reiter__knopf[aria-expanded="true"]');
  if (!(reiter instanceof HTMLElement)) return false;
  reiter.click();
  return true;
}

/**
 * Das Zeichen des Knopfs: die Flaeche des Fensters, gezeichnet.
 *
 * Schwebend liegt sie als flacher Streifen ganz unten im Zeichenfeld, ein
 * Winkel nach unten darueber - dorthin faehrt das Fenster zusammen. Im
 * Leisten-Modus fuellt sie den unteren Teil des Feldes, der Winkel zeigt
 * nach oben - von dort kommt es zurueck. Ein Rechteck, das seine Masse
 * faehrt; die Bewegung kommt aus dem Stilblatt.
 *
 * @returns {HTMLElement}
 */
function anheftZeichen() {
  const huelle = document.createElement('span');
  huelle.className = 'dp-anheft';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const flaeche = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  flaeche.setAttribute('class', 'dp-anheft__flaeche');
  svg.appendChild(flaeche);

  for (const [klasse, d] of [
    ['dp-anheft__runter', 'M8.5 7.5 12 11l3.5-3.5'],
    ['dp-anheft__hoch', 'M8.5 6.5 12 3l3.5 3.5'],
  ]) {
    const winkel = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    winkel.setAttribute('class', klasse);
    winkel.setAttribute('d', d);
    svg.appendChild(winkel);
  }

  svg.setAttribute('class', 'dp-anheft__lage');
  huelle.appendChild(svg);

  /*
   * Die zweite Fassung des Zeichens, fuer die offene Grossansicht: zwei
   * Rechtecke uebereinander, oben die Grossansicht, unten das Kauffenster -
   * jedes so hoch, wie sein Fenster gerade vom Bildschirm einnimmt. Der
   * Knopf ist dann kein Knopf, sondern eine kleine Anzeige des Modus
   * (Stilblatt: ohne Kreis, nicht antippbar). Die Masse setzt
   * pflegeModusZeichen.
   */
  const modus = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  modus.setAttribute('class', 'dp-anheft__modus');
  modus.setAttribute('viewBox', '0 0 24 24');
  modus.setAttribute('aria-hidden', 'true');
  modus.setAttribute('focusable', 'false');
  for (const klasse of ['dp-anheft__bild', 'dp-anheft__kauf']) {
    const teil = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    teil.setAttribute('class', klasse);
    teil.setAttribute('x', '4');
    teil.setAttribute('width', '16');
    teil.setAttribute('rx', '1.5');
    modus.appendChild(teil);
  }
  huelle.appendChild(modus);

  return huelle;
}

/**
 * Setzt die beiden Rechtecke der Modus-Anzeige auf die Anteile, die
 * Grossansicht und Kauffenster gerade vom Bildschirm einnehmen.
 *
 * Gezeichnet wird in einem Feld von 14 Einheiten Hoehe (4 bis 20 im Zeichen),
 * zwei Einheiten Luft zwischen beiden. Ein Fenster, das die halbe Hoehe des
 * Bildschirms hat, bekommt also ein Rechteck von sieben Einheiten. Laeuft
 * bei jeder Groessenaenderung mit; ohne offene Grossansicht gibt es nichts
 * zu tun.
 */
function pflegeModusZeichen() {
  if (document.documentElement.dataset.dpBildfeld !== 'offen') return;
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf) return;

  /*
   * Gemessen wird die Layout-Hoehe (offsetHeight), nicht der gezeichnete
   * Kasten: die Grossansicht faehrt beim Oeffnen aus der Kachel heraus, und
   * waehrend dieser Fahrt meldet der gezeichnete Kasten die Zwischengroesse.
   * Auf dem iPhone fiel die Messung regelmaessig in diese Fahrt - das obere
   * Rechteck blieb dann winzig. Die Layout-Hoehe steht von Anfang an fest.
   */
  /*
   * Der Rahmen der offenen Grossansicht - nicht irgendeiner: jede Galerie im
   * Dokument (auch die unsichtbaren Fassungen fuer andere Breiten) traegt
   * eine eigene Grossansicht, und die erste im Dokument ist meist eine
   * verborgene ohne Hoehe. Damit stand das obere Rechteck nie richtig.
   */
  const rahmen = document.querySelector(
    '.dp-bildfeld:not([hidden]):not(.dp-bildfeld--korb) .dp-bildfeld__rahmen'
  );
  const schirm = window.innerHeight || 1;
  const bildAnteil = rahmen instanceof HTMLElement ? rahmen.offsetHeight / schirm : 0;
  const kaufAnteil = kauf.offsetHeight / schirm;

  const FELD = 14;
  const LUFT = 2;
  const bildHoehe = Math.max(1, Math.round(bildAnteil * FELD * 10) / 10);
  const kaufHoehe = Math.max(1, Math.round(kaufAnteil * FELD * 10) / 10);

  for (const zeichen of kauf.querySelectorAll('.dp-anheft__modus')) {
    const bild = zeichen.querySelector('.dp-anheft__bild');
    const kaufRect = zeichen.querySelector('.dp-anheft__kauf');
    if (!bild || !kaufRect) continue;
    bild.setAttribute('y', '4');
    bild.setAttribute('height', String(bildHoehe));
    kaufRect.setAttribute('y', String(Math.round((4 + bildHoehe + LUFT) * 10) / 10));
    kaufRect.setAttribute('height', String(kaufHoehe));
  }
}

/**
 * Verwirft den Knopf, wo er nichts zu tun haette.
 *
 * Sind die Bilder so kurz, dass das Fenster schon ohne Rollen unter ihnen
 * Platz hat, schwebt es nie am unteren Rand - es gibt dann nichts, was sich
 * zusammenfahren liesse. Gerechnet wird in Dokumentmassen, also unabhaengig
 * vom Rollstand. Der Name macht nur Platz, wenn der Knopf auch da ist.
 */
function pflegePin() {
  const kauf = document.querySelector('.dp-kauffenster');
  const knopf = kauf && kauf.querySelector('.dp-kauffenster__zu');
  if (!kauf || !knopf) return;

  let ueberfluessig = false;
  if (!BREIT.matches) {
    const galerie = holeGalerie();
    if (galerie) {
      const untenImDokument = galerie.getBoundingClientRect().bottom + window.scrollY;
      ueberfluessig =
        untenImDokument + LUFT_ZUM_BILD + natuerlicheHoehe(kauf) <= window.innerHeight - luftUnten();
    }
  }

  if (knopf.classList.contains('dp-kauffenster__zu--weg') !== ueberfluessig) {
    knopf.classList.toggle('dp-kauffenster__zu--weg', ueberfluessig);
  }
  if (kauf.classList.contains('dp-kauffenster--mit-knopf') === ueberfluessig) {
    kauf.classList.toggle('dp-kauffenster--mit-knopf', !ueberfluessig);
  }
  /*
   * Der Modus selbst bleibt bestehen, auch ohne Knopf. Bei kurzen Bildern
   * fuehren seine Regeln ohnehin zum ganzen Fenster unter den Bildern -
   * und waechst die Galerie noch (spaet ladende Bilder, die IMPRINT-Ansichten
   * ziehen nach), greift die Wahl des Besuchers wieder, statt dass sie
   * beim Laden still verworfen wuerde.
   */
}

/**
 * Schaltet den Leisten-Modus an oder aus.
 *
 * An: das Fenster faehrt dort, wo es steht, zur Leiste zusammen - es sei
 * denn, es passt ohnehin unter die Bilder, dann bleibt es dort. Aus: die
 * Leiste faehrt am unteren Rand zum Fenster auf, danach gilt wieder die
 * gewohnte Lage (schwebend oder an der Bildkante).
 *
 * @param {boolean} an
 */
/** Schluessel im Sitzungsspeicher: ob der Besucher die Leiste gewaehlt hat. */
const LEISTE_MERKER = 'dp-kauf-leiste';

/**
 * Merkt sich die Wahl fuer die Sitzung - auf der naechsten Produktseite
 * beginnt das Fenster dann gleich als Leiste. Der Speicher kann fehlen
 * (private Fenster, alte Geraete); dann gilt die Wahl eben nur hier.
 *
 * @param {boolean} an
 */
function merkeLeiste(an) {
  try {
    window.sessionStorage.setItem(LEISTE_MERKER, an ? '1' : '0');
  } catch {
    /* kein Speicher - kein Merken */
  }
}

/** @returns {boolean} */
function gemerkteLeiste() {
  try {
    return window.sessionStorage.getItem(LEISTE_MERKER) === '1';
  } catch {
    return false;
  }
}

/**
 * @param {boolean} an
 * @param {boolean} [merken] Ob die Wahl als Wahl des Besuchers gilt. Nicht,
 *   wenn das Skript den Modus selbst beendet - etwa weil die Galerie zu kurz
 *   ist oder das Fenster breit wird: die Wahl fuer die naechste Seite bleibt.
 */
function schalteAndocken(an, merken = true) {
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf || BREIT.matches) return;
  if (merken) merkeLeiste(an);
  if (an === angedockt) return;

  angedockt = an;
  kauf.classList.toggle(ANGEDOCKT, an);

  const knopf = kauf.querySelector('.dp-kauffenster__zu');
  if (knopf) {
    knopf.setAttribute('aria-pressed', an ? 'true' : 'false');
    knopf.setAttribute('aria-label', an ? LOESE_WORT : ANDOCK_WORT);
  }

  /*
   * Bei offener Grossansicht steht das Fenster ohnehin ganz am unteren Rand;
   * der Wechsel greift erst, wenn sie zu ist.
   */
  if (document.documentElement.dataset.dpBildfeld === 'offen') return;

  if (an) {
    const galerie = holeGalerie();
    const spalte = document.querySelector('.product-details');
    if (galerie && spalte) lageMinimiert(kauf, galerie, spalte);
    return;
  }

  if (istKlein(kauf)) {
    fahreAuf(kauf, luftUnten(), haltAmBildende);
    return;
  }
  haltAmBildende();
}

/* ------------------------------------------------- Die schmale Kaufleiste */

/**
 * Baut die Leiste einmalig in das Fenster.
 *
 * Sie liegt im Fenster selbst und teilt sich mit ihm Flaeche, Glas und
 * Rundung; gezeigt wird sie nur, wenn das Fenster zusammengefahren ist.
 *
 * @param {HTMLElement} kauf
 */
function baueLeiste(kauf) {
  if (kauf.querySelector('.dp-kaufleiste')) return;

  const leiste = document.createElement('div');
  leiste.className = 'dp-kaufleiste';

  const text = document.createElement('div');
  text.className = 'dp-kaufleiste__text';

  const name = document.createElement('div');
  name.className = 'dp-kaufleiste__name';

  // Preis und Farbpunkte teilen sich die zweite Zeile.
  const zeile = document.createElement('div');
  zeile.className = 'dp-kaufleiste__zeile';
  const preis = document.createElement('div');
  preis.className = 'dp-kaufleiste__preis';
  const farben = document.createElement('div');
  farben.className = 'dp-kaufleiste__farben';
  zeile.append(preis, farben);
  text.append(name, zeile);

  const knopf = document.createElement('button');
  knopf.type = 'button';
  knopf.className = 'button dp-kaufleiste__knopf';
  knopf.addEventListener('click', leistenKlick);

  // Ganz rechts derselbe Knopf wie im Fenster: faehrt die Leiste wieder auf.
  const anheften = document.createElement('button');
  anheften.type = 'button';
  anheften.className = 'dp-kaufleiste__anheften';
  anheften.setAttribute('aria-label', LOESE_WORT);
  anheften.appendChild(anheftZeichen());
  anheften.addEventListener('click', () => schalteAndocken(false));

  leiste.append(text, knopf, anheften);
  kauf.appendChild(leiste);

  fuelleLeiste();
}

/**
 * Die Farbpunkte der Leiste: je Farbe des Artikels ein Kreis, der gewaehlte
 * mit Ring - dieselben Punkte wie auf den Kacheln der Startseite. Ein Tipp
 * waehlt die Farbe im Fenster, genau wie ein Tipp auf das Farbfeld dort.
 *
 * Neu gebaut wird nur, wenn sich Farben oder Wahl geaendert haben.
 *
 * @param {HTMLElement} kauf
 * @param {HTMLElement} leiste
 */
function fuelleFarben(kauf, leiste) {
  const farben = leiste.querySelector('.dp-kaufleiste__farben');
  if (!(farben instanceof HTMLElement)) return;

  const felder = [
    ...kauf.querySelectorAll('.variant-option--swatches label.variant-option__button-label--has-swatch'),
  ]
    .map((feld) => {
      const eingabe = feld.querySelector('input[type="radio"]');
      const tupfer = feld.querySelector('.swatch');
      if (!(eingabe instanceof HTMLInputElement) || !(tupfer instanceof HTMLElement)) return null;
      return {
        eingabe,
        name: eingabe.value,
        farbe: tupfer.style.getPropertyValue('--swatch-background').trim(),
        gewaehlt: eingabe.checked,
        lieferbar: eingabe.dataset.optionAvailable !== 'false',
      };
    })
    .filter(Boolean);

  const kennung = felder
    .map((f) => f.name + ':' + f.farbe + ':' + (f.gewaehlt ? 1 : 0) + (f.lieferbar ? '' : 'x'))
    .join('|');
  if (farben.dataset.dpKennung === kennung) return;
  farben.dataset.dpKennung = kennung;

  farben.replaceChildren();
  for (const feld of felder) {
    const punkt = document.createElement('button');
    punkt.type = 'button';
    punkt.className = 'dp-kaufleiste__farbe' + (feld.gewaehlt ? ' dp-kaufleiste__farbe--aktiv' : '');
    punkt.style.setProperty('--dp-farbe', feld.farbe);
    punkt.setAttribute('aria-label', feld.name);
    punkt.setAttribute('aria-pressed', feld.gewaehlt ? 'true' : 'false');
    if (!feld.lieferbar) punkt.classList.add('dp-kaufleiste__farbe--aus');
    punkt.addEventListener('click', () => {
      // Das Farbfeld im Fenster meldet die Wahl weiter - das Theme wechselt die Variante.
      if (!feld.eingabe.checked) feld.eingabe.click();
    });
    farben.appendChild(punkt);
  }
}

/*
 * Das Zeichen im Knopf der Leiste: dasselbe wie am Kaufknopf im Fenster
 * (assets/icon-add-to-cart.svg), Einkaufswagen mit Plus an der oberen rechten Korbecke, Kontur dort ausgespart (Oktober 2026, vorher Tasche).
 * Vorher stand hier der Warenkorb des Headers ohne Plus - die Leiste zeigte
 * dadurch einen anderen Knopf als das offene Fenster (Jimmi, September 2026).
 * Fehlt noch eine Angabe, legt das Stilblatt den Stift darueber und blendet
 * den Wagen aus (dp-kaufknopf--perso).
 *
 * Das Zeichen wird hier gezeichnet, nicht aus dem Fensterknopf kopiert: dort
 * legt das Theme nicht immer eines hinein, und der Knopf blieb sonst leer.
 */
const WAGEN_ZEICHEN = [
  'M2 3h2.6l2.4 11a1.6 1.6 0 0 0 1.6 1.3h8a1.6 1.6 0 0 0 1.55-1.2l1.35-4.2M5.6 7.6h9M19.4 2.6v5M16.9 5.1h5M7.7 19.6a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0M14.7 19.6a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0',
];

function kaufZeichen() {
  const raum = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(raum, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', 'dp-kaufleiste__zeichen');
  for (const d of WAGEN_ZEICHEN) {
    const pfad = document.createElementNS(raum, 'path');
    pfad.setAttribute('d', d);
    pfad.setAttribute('stroke', 'currentColor');
    pfad.setAttribute('stroke-width', 'var(--icon-stroke-width)');
    pfad.setAttribute('stroke-linecap', 'round');
    pfad.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(pfad);
  }
  return svg;
}

/*
 * Die Leiste zieht nach, sobald sich der Kaufknopf im Fenster aendert: gesperrt
 * oder frei, Wagen oder Stift. Beobachtet wird allein dieser eine Knopf, kein
 * dokumentweiter Blick.
 *
 * Noetig, weil der Knopf beim Laden der Seite noch gesperrt dasteht und erst
 * danach freigegeben wird. Wer mit zusammengefahrener Leiste ankommt, sah sie
 * sonst bis zur ersten Eingabe ausgegraut.
 *
 * @param {HTMLElement} echt
 */
function beobachteKaufknopf(echt) {
  if (echt.dpLeisteBeobachtet) return;
  echt.dpLeisteBeobachtet = true;
  const blick = new MutationObserver(() => fuelleLeiste());
  blick.observe(echt, { attributes: true, attributeFilter: ['disabled', 'class', 'aria-label'] });
}

/*
 * Waehrend der Warenkorb-Knopf laedt, ist das ganze Kauffenster gesperrt
 * (Jimmi, Oktober 2026): Eine Farbe, die waehrenddessen geaendert wurde, kam
 * mit in den Warenkorb, das Vorschaubild aber zeigte noch die alte. Gesperrt
 * wird alles ausser dem Knopf selbst - je Ebene seine Geschwister (inert:
 * kein Tipp, keine Tastatur), sichtbar ausgegraut und weich eingeblendet.
 * Gilt fuer jede App, die den Knopf laden laesst (IMPRINT, SOLID, LAYER:
 * dp-kaufknopf--laedt). Die Sperre selbst ist ein gemeinsamer Baustein
 * (dp-laden.js, sperreUm) - Formulare und Bewertung sperren genauso.
 *
 * @param {HTMLElement} kauf
 * @param {HTMLElement} echt der Warenkorb-Knopf im Fenster
 */
/*
 * Was beim Laden nicht ausgegraut wird: die Lieferzeile, in der der Text zum
 * Ladering steht (dp-kauf-fortschritt.js) - er spricht ja gerade vom Laden
 * (Jimmi, Oktober 2026). Liegt er tiefer, wird um ihn herum gesperrt.
 */
const FREI_BEIM_LADEN = '.dp-lieferung';

function pflegeKaufSperre(kauf, echt) {
  const an = echt.classList.contains('dp-kaufknopf--laedt');
  if (an === !!kauf.dpKaufSperre) return;
  if (an) {
    // Das Wischen haelt das Kauffenster selbst (haelteWischen).
    kauf.dpKaufSperre = sperreUm(kauf, echt, { frei: FREI_BEIM_LADEN, wischen: false });
  } else {
    kauf.dpKaufSperre();
    kauf.dpKaufSperre = null;
  }
  kauf.classList.toggle('dp-kauffenster--gesperrt', an);
}

/**
 * Traegt Name, Preis, Farben und den Zustand des Kaufknopfs aus dem Fenster
 * in die Leiste - nur bei Abweichung, die Funktion laeuft bei jeder Eingabe mit.
 */
function fuelleLeiste() {
  const kauf = document.querySelector('.dp-kauffenster');
  const leiste = kauf && kauf.querySelector('.dp-kaufleiste');
  if (!kauf || !(leiste instanceof HTMLElement)) return;

  const titel = kauf.querySelector('h1');
  const name = leiste.querySelector('.dp-kaufleiste__name');
  if (titel && name) {
    const wort = titel.textContent.trim();
    if (name.textContent !== wort) name.textContent = wort;
  }

  /*
   * Der Preis kommt samt Markup: so stehen Streichpreis und Vorlesetexte
   * genauso da wie im Fenster, und das Stilblatt kennt die Klassen schon.
   */
  const quelle = kauf.querySelector('product-price [ref="priceContainer"]');
  const preis = leiste.querySelector('.dp-kaufleiste__preis');
  if (quelle && preis && preis.innerHTML !== quelle.innerHTML) preis.innerHTML = quelle.innerHTML;

  fuelleFarben(kauf, leiste);

  const echt = kauf.querySelector('add-to-cart-component button');
  const knopf = leiste.querySelector('.dp-kaufleiste__knopf');
  if (echt instanceof HTMLElement) pflegeKaufSperre(kauf, echt);
  if (!(echt instanceof HTMLElement) || !(knopf instanceof HTMLElement)) return;

  beobachteKaufknopf(echt);

  if (!knopf.firstElementChild) knopf.appendChild(kaufZeichen());

  /*
   * Dieselben Masse wie der Knopf im Fenster. Seine Hoehe ergibt sich dort
   * aus der Zeile mit der Mengenwahl - also wird gemessen statt geraten.
   * Er steht auch in der Leiste im Aufbau (nur ausgeblendet), die Masse
   * gibt es also immer.
   */
  const breite = echt.offsetWidth;
  const hoehe = echt.offsetHeight;
  if (breite && hoehe) {
    if (leiste.style.getPropertyValue('--dp-kaufknopf-breite') !== breite + 'px') leiste.style.setProperty('--dp-kaufknopf-breite', breite + 'px');
    if (leiste.style.getPropertyValue('--dp-kaufknopf-hoehe') !== hoehe + 'px') leiste.style.setProperty('--dp-kaufknopf-hoehe', hoehe + 'px');
  }

  /*
   * Die Zustaende des echten Knopfs gelten auch fuer die Leiste: der Stift
   * (perso), die fehlende Farbe und der Ladering. Frueher kam nur der Stift
   * mit; die Rechner sahen der Leiste die fehlende Farbe deshalb nicht an und
   * behandelten ihren Klick anders als den im Fenster.
   */
  for (const marke of ['dp-kaufknopf--perso', 'dp-kaufknopf--ohne-farbe', 'dp-kaufknopf--laedt']) {
    const da = echt.classList.contains(marke);
    if (knopf.classList.contains(marke) !== da) knopf.classList.toggle(marke, da);
  }
  const perso = echt.classList.contains('dp-kaufknopf--perso');
  const wort = echt.getAttribute('aria-label') || 'In den Warenkorb legen';
  if (knopf.getAttribute('aria-label') !== wort) knopf.setAttribute('aria-label', wort);
  if (knopf.disabled !== echt.disabled) knopf.disabled = echt.disabled;
}

/*
 * Beim Auf- und Zufahren schweben Kaufknopf, Name, Preis und Farbpunkte an
 * ihre neue Stelle.
 *
 * Fenster und Leiste haben je eigene Elemente, und beim Wechsel blendete das
 * eine aus und das andere ein - alles verschwand an der einen Stelle und
 * tauchte hart an der anderen auf. Jetzt fliegt fuer jedes Paar ein Doppel
 * von der alten zur neuen Stelle, waehrend die echten Elemente verborgen
 * warten:
 *
 *   Kaufknopf - ein Doppel des Leistenknopfs; beide sehen gleich aus und
 *     sind gleich gross, er gleitet nur.
 *   Name, Preis, jeder Farbpunkt - zwei Doppel uebereinander, eines in der
 *     Fassung des Fensters, eines in der der Leiste. Sie ziehen gemeinsam
 *     von Groesse und Lage der einen zur anderen und blenden dabei
 *     ineinander ueber.
 *
 * Die Endstellen werden vor der Fahrt gemessen, nicht waehrend: der Inhalt
 * des Fensters und die Leiste richten sich an verschiedenen Kanten aus, und
 * ein Ziel, das waehrend der Fahrt mitwandert, zog das Doppel erst in die
 * falsche Richtung - der Knopf hopste beim Zufahren nach oben, statt nur
 * nach links zu gleiten. Gemessen wird, indem der Endzustand fuer einen
 * Augenblick ohne Uebergang gesetzt und gleich wieder zurueckgenommen wird -
 * zwischen beidem zeichnet der Browser nicht, zu sehen ist davon nichts.
 *
 * Die Doppel liegen im Fenster selbst, nicht in Leiste oder Inhalt - die
 * blenden waehrend der Fahrt aus und ein, und die Doppel sollen dabei ihre
 * eigene Deckkraft haben. Dauer und Kurve sind die der Fahrt (Stilblatt,
 * dp-kauffenster--faehrt), so kleben sie am Fenster.
 */
const GEIST_DAUER = 320;
let geistLauf = null;

/* Was ein Doppel von seinem Vorbild mitnimmt - die Regeln des Stilblatts greifen ausserhalb seines Platzes nicht mehr. */
const GEIST_STIL = [
  'box-sizing', 'display', 'flex-direction', 'flex-wrap', 'align-items', 'justify-content', 'gap',
  'width', 'height', 'min-width', 'min-height',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'font-family', 'font-size', 'font-weight', 'font-style', 'font-variant-numeric', 'line-height', 'letter-spacing',
  'text-transform', 'text-decoration-line', 'white-space', 'text-overflow', 'overflow', 'vertical-align',
  'color', 'opacity', 'background-color', 'background-image',
  'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',
  'border-top-style', 'border-right-style', 'border-bottom-style', 'border-left-style',
  'border-top-color', 'border-right-color', 'border-bottom-color', 'border-left-color',
  'border-top-left-radius', 'border-top-right-radius', 'border-bottom-right-radius', 'border-bottom-left-radius',
  'box-shadow', 'outline-style', 'outline-width', 'outline-color', 'outline-offset',
];

/**
 * Ein Doppel mit dem Aussehen seines Vorbilds - die berechneten Werte
 * werden Element fuer Element mitgegeben.
 *
 * @param {Element} vorbild
 * @returns {HTMLElement}
 */
function stilDoppel(vorbild) {
  const doppel = /** @type {HTMLElement} */ (vorbild.cloneNode(true));
  const quellen = [vorbild, ...vorbild.querySelectorAll('*')];
  const kopien = [doppel, ...doppel.querySelectorAll('*')];
  quellen.forEach((quelle, i) => {
    const kopie = kopien[i];
    if (!kopie || !kopie.style) return;
    const stil = getComputedStyle(quelle);
    for (const name of GEIST_STIL) kopie.style.setProperty(name, stil.getPropertyValue(name));
    kopie.style.setProperty('transition', 'none');
    kopie.style.setProperty('animation', 'none');
    /*
     * Die Verschiebung innen bleibt, wie sie gerade ist - etwa ein wandernder
     * Name mitten in seiner Reise (dp-lauftext.js). Nur das Doppel selbst
     * bekommt seine eigene, die des Flugs.
     */
    kopie.style.setProperty('transform', i === 0 ? 'none' : stil.getPropertyValue('transform'));
    kopie.removeAttribute('id');
    // Von Hand gewischt (etwa der lange Name): der Stand wird nach dem Einhaengen uebertragen - siehe rolleDoppel.
    if (quelle.scrollLeft) (doppel.dpRollen || (doppel.dpRollen = [])).push([kopie, quelle.scrollLeft]);
  });
  return doppel;
}

/** Uebertraegt den gewischten Stand aufs Doppel - geht erst, wenn es im Dokument haengt. */
function rolleDoppel(doppel) {
  for (const [kopie, weite] of doppel.dpRollen || []) kopie.scrollLeft = weite;
}

/** Die Kurve der Fahrt (--dp-ease) als Funktion - ein cubic-bezier, nachgerechnet. */
function fahrtKurve() {
  const wert = getComputedStyle(document.documentElement).getPropertyValue('--dp-ease');
  const zahlen = (wert.match(/cubic-bezier\(([^)]+)\)/) || [])[1];
  const [x1, y1, x2, y2] = zahlen ? zahlen.split(',').map(Number) : [0.22, 1, 0.36, 1];
  const ax = 1 - 3 * x2 + 3 * x1;
  const bx = 3 * x2 - 6 * x1;
  const cx = 3 * x1;
  const ay = 1 - 3 * y2 + 3 * y1;
  const by = 3 * y2 - 6 * y1;
  const cy = 3 * y1;
  return (t) => {
    let u = t;
    for (let i = 0; i < 8; i += 1) {
      const x = ((ax * u + bx) * u + cx) * u - t;
      const dx = (3 * ax * u + 2 * bx) * u + cx;
      if (Math.abs(x) < 1e-5 || Math.abs(dx) < 1e-6) break;
      u -= x / dx;
    }
    u = Math.min(1, Math.max(0, u));
    return ((ay * u + by) * u + cy) * u;
  };
}

/**
 * Die Paare, die schweben: je ein Element im Fenster und eines in der Leiste.
 *
 * @param {HTMLElement} kauf
 * @returns {{ fenster: HTMLElement, leiste: HTMLElement, knopf?: boolean }[]}
 */
function flugPaare(kauf) {
  const leiste = kauf.querySelector('.dp-kaufleiste');
  if (!(leiste instanceof HTMLElement)) return [];
  const paare = [];
  const paar = (fenster, inLeiste, knopf) => {
    if (fenster instanceof HTMLElement && inLeiste instanceof HTMLElement) paare.push({ fenster, leiste: inLeiste, knopf });
  };
  paar(kauf.querySelector('add-to-cart-component button'), leiste.querySelector('.dp-kaufleiste__knopf'), true);
  paar(kauf.querySelector('.group-block-content h1'), leiste.querySelector('.dp-kaufleiste__name'));
  paar(kauf.querySelector('product-price [ref="priceContainer"]'), leiste.querySelector('.dp-kaufleiste__preis'));
  /* Die Farbpunkte einzeln - die Leiste baut sie in derselben Reihenfolge aus den Farbfeldern (fuelleFarben). */
  const tupfer = [...kauf.querySelectorAll('.variant-option--swatches label.variant-option__button-label--has-swatch')]
    .filter((feld) => feld.querySelector('input[type="radio"]'))
    .map((feld) => feld.querySelector('.swatch'));
  const punkte = [...leiste.querySelectorAll('.dp-kaufleiste__farbe')];
  tupfer.forEach((t, i) => paar(t, punkte[i]));
  return paare;
}

/**
 * Misst, wo die Ziele am Ende der Fahrt stehen: der Endzustand wird ohne
 * Uebergang gesetzt, abgelesen und sofort zurueckgenommen.
 *
 * @param {HTMLElement} kauf
 * @param {HTMLElement[]} ziele
 * @param {() => void} endstand Setzt Klasse und Masse des Endzustands.
 * @returns {DOMRect[]}
 */
function messeEnde(kauf, ziele, endstand) {
  const stil = kauf.getAttribute('style');
  const klasse = kauf.className;
  kauf.style.setProperty('transition', 'none', 'important');
  endstand();
  kauf.style.setProperty('transition', 'none', 'important');
  const stellen = ziele.map((ziel) => ziel.getBoundingClientRect());
  // Zurueck - erst ohne Uebergang, sonst fuehre das Fenster vom Ende zum Anfang.
  kauf.className = klasse;
  if (stil === null) kauf.removeAttribute('style');
  else kauf.setAttribute('style', stil);
  kauf.style.setProperty('transition', 'none', 'important');
  void kauf.offsetHeight;
  if (stil === null) kauf.removeAttribute('style');
  else kauf.setAttribute('style', stil);
  void kauf.offsetHeight;
  return stellen;
}

/**
 * Laesst die Paare schweben. Aufzurufen unmittelbar bevor die Fahrt den
 * Endzustand setzt.
 *
 * @param {HTMLElement} kauf
 * @param {boolean} zurLeiste true: vom Fenster in die Leiste, false: zurueck.
 * @param {() => void} endstand Setzt Klasse und Masse des Endzustands (fuer die Messung).
 */
function schwebeInhalt(kauf, zurLeiste, endstand) {
  if (geistLauf) geistLauf.ende();

  /*
   * Beim Auffahren steht der Name vorn - auch wenn ihn der Kunde vorher von
   * Hand zur Seite gewischt hat. Die Wander-Animation setzt ohnehin neu an
   * (Stilblatt, dp-kauffenster--klein); das Wischen verschiebt das Feld
   * selbst und blieb sonst stehen.
   */
  if (!zurLeiste) {
    for (const name of kauf.querySelectorAll('.text-block :is(h1, h2, h3)')) {
      if (name.scrollLeft) name.scrollLeft = 0;
    }
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const paare = flugPaare(kauf)
    .map((p) => ({ von: zurLeiste ? p.fenster : p.leiste, nach: zurLeiste ? p.leiste : p.fenster, knopf: !!p.knopf }))
    .map((p) => ({ ...p, start: p.von.getBoundingClientRect() }))
    // Am Rechner gibt es keine Leiste, und was nicht zu sehen ist, fliegt nicht.
    .filter((p) => p.start.width && p.start.height && p.nach.offsetWidth);
  if (!paare.length) return;

  const enden = messeEnde(kauf, paare.map((p) => p.nach), endstand);

  const geister = [];
  for (const [i, p] of paare.entries()) {
    const ziel = enden[i];
    if (!ziel.width || !ziel.height) continue;
    const massstab = ziel.height / p.start.height;
    const doppel = [];
    if (p.knopf) {
      // Der Leistenknopf als Vorbild: seine Regeln gelten ueberall, und beide Knoepfe sehen gleich aus.
      const leistenKnopf = zurLeiste ? p.nach : p.von;
      const g = /** @type {HTMLElement} */ (leistenKnopf.cloneNode(true));
      g.style.width = p.start.width + 'px';
      g.style.minWidth = p.start.width + 'px';
      g.style.height = p.start.height + 'px';
      g.style.minHeight = p.start.height + 'px';
      g.style.transition = 'none';
      doppel.push({ el: g, von: 1, bis: 1, blende: null });
    } else {
      doppel.push({ el: stilDoppel(p.von), von: 1, bis: massstab, blende: 'aus' });
      doppel.push({ el: stilDoppel(p.nach), von: 1 / massstab, bis: 1, blende: 'ein' });
    }
    for (const d of doppel) {
      d.el.classList.add('dp-kaufknopf-geist');
      d.el.setAttribute('aria-hidden', 'true');
      d.el.removeAttribute('aria-label');
      d.el.tabIndex = -1;
      d.el.style.position = 'absolute';
      d.el.style.margin = '0';
      d.el.style.transformOrigin = '0 0';
      d.el.style.pointerEvents = 'none';
      if (d.blende === 'ein') d.el.style.opacity = '0';
      kauf.appendChild(d.el);
      rolleDoppel(d.el);
    }
    p.von.style.visibility = 'hidden';
    p.nach.style.visibility = 'hidden';
    geister.push({ paar: p, ziel, doppel });
  }

  const kurve = fahrtKurve();
  const setze = (e) => {
    const k = kauf.getBoundingClientRect();
    for (const { paar, ziel, doppel } of geister) {
      const x = paar.start.left + (ziel.left - paar.start.left) * e - k.left - kauf.clientLeft;
      const y = paar.start.top + (ziel.top - paar.start.top) * e - k.top - kauf.clientTop;
      for (const d of doppel) {
        d.el.style.left = Math.round(x * 10) / 10 + 'px';
        d.el.style.top = Math.round(y * 10) / 10 + 'px';
        const s = d.von + (d.bis - d.von) * e;
        d.el.style.transform = 'scale(' + s.toFixed(4) + ')';
        if (d.blende === 'aus') d.el.style.opacity = String(1 - e);
        if (d.blende === 'ein') d.el.style.opacity = String(e);
      }
    }
  };
  setze(0);

  const t0 = performance.now();
  let bild = 0;
  const schritt = (jetzt) => {
    const t = Math.min(1, (jetzt - t0) / GEIST_DAUER);
    setze(kurve(t));
    if (t < 1) bild = requestAnimationFrame(schritt);
    else lauf.ende();
  };

  const lauf = {
    ende() {
      cancelAnimationFrame(bild);
      for (const { paar, doppel } of geister) {
        for (const d of doppel) d.el.remove();
        paar.von.style.removeProperty('visibility');
        paar.nach.style.removeProperty('visibility');
      }
      if (geistLauf === lauf) geistLauf = null;
    },
  };
  geistLauf = lauf;
  bild = requestAnimationFrame(schritt);
  // Die Reissleine, falls keine Bilder kommen (Tab im Hintergrund).
  window.setTimeout(() => {
    if (geistLauf === lauf) lauf.ende();
  }, GEIST_DAUER + 400);
}

/**
 * Der Knopf in der Leiste tut, was der Knopf im Fenster tun wuerde.
 *
 * Fehlt noch eine Angabe, ist das Fenster gefragt: die Leiste faehrt auf,
 * und der Klick am echten Knopf oeffnet dort wie gewohnt den Reiter mit den
 * Feldern (dp-personalisierung.js). Sonst legt der Klick den Artikel in den
 * Warenkorb, und die Leiste bleibt.
 */
function leistenKlick() {
  const kauf = document.querySelector('.dp-kauffenster');
  const echt = kauf && kauf.querySelector('add-to-cart-component button');
  if (!(echt instanceof HTMLElement)) return;

  const braucht =
    echt.classList.contains('dp-kaufknopf--perso') || echt.classList.contains('dp-kaufknopf--ohne-farbe');

  if (braucht) schalteAndocken(false);
  echt.click();
}

/**
 * Stellt sicher, dass das Fenster Knopf und Leiste traegt.
 *
 * Beim Farb- oder Groessenwechsel zeichnet das Theme den Abschnitt neu und
 * kann das Fenster dabei ersetzen - das neue kennt weder Knopf noch Leiste
 * noch den Zustand. Hier bekommt es alles zurueck; die Lage entscheidet
 * danach haltAmBildende.
 */
function ruesteAus() {
  const kauf = document.querySelector('.dp-kauffenster');
  if (!kauf) return;

  const frisch = !kauf.querySelector('.dp-kaufleiste');
  baueAndockKnopf(kauf);
  baueLeiste(kauf);
  fuelleLeiste();

  if (frisch && angedockt) {
    kauf.classList.add(ANGEDOCKT);
    /*
     * Stand das alte Fenster als Leiste, beginnt das neue auch so - am
     * unteren Rand, ohne Fahrt. Wohin es von dort gehoert (an die Bildkante,
     * aufgefahren), entscheidet gleich darauf der weiche Weg ueber
     * pruefeLageWeich: die neue Galerie hat womoeglich eine andere Hoehe.
     */
    if (leisteAktiv && !BREIT.matches) {
      kauf.classList.add(KLEIN);
      kauf.style.setProperty('height', LEISTE_HOEHE + 'px');
      schwebe(kauf);
      fuelleLeiste();
    }
    if (!BREIT.matches) pruefeLageSpaeter();
  }
  pflegePin();
}

/*
 * Nur Groesse: hat der Artikel genau EINE Auswahl und ist die ein Dropdown mit
 * mehr als einer Groesse (keine Farbfelder, keine Wahl mit nur einer
 * Moeglichkeit), steht die Groesse unten in der Kaufzeile (Stilblatt, Abschnitt
 * "Nur Groesse"). Die Marke sitzt auf <html>: das Theme zeichnet beim
 * Groessenwechsel den Abschnitt neu und nimmt dem Fenster dabei Klassen weg -
 * die Kaufzeile spraenge sonst zurueck. Konfigurator-Fenster nimmt das Stilblatt
 * selbst aus (transfer, imprint, solid, layer).
 */
function pflegeNurGroesse() {
  const kauf = document.querySelector('.dp-kauffenster');
  const bild = kauf ? kauf.querySelector('variant-picker') : null;
  /* Mitten im Neuzeichnen fehlt der Waehler kurz: dann bleibt die Marke, wie sie ist. */
  if (!bild) return;
  let an = false;
  {
    const optionen = bild.querySelectorAll('.variant-option');
    const wahl = optionen.length === 1 ? optionen[0] : null;
    if (wahl && wahl.classList.contains('variant-option--dropdowns') && !bild.querySelector('.variant-option--swatches')) {
      const menue = wahl.querySelector('select');
      an = !!menue && [...menue.options].filter((option) => option.value).length > 1;
    }
  }
  document.documentElement.classList.toggle('dp-kauf-nur-groesse', an);
  pflegeGroesseKurz();
}

/*
 * Die Groessenzeile nutzt die Kurzform des Lieferhinweises ebenfalls (siehe "Nur Farbe"):
 * ist der Hinweis dort schmaler als HINWEIS_LANG, steht "5 bis 8 Werktage" statt des
 * wandernden Langtextes. Seine Breite (1fr-Spalte) haengt nicht vom Inhalt ab - keine Rueckkopplung.
 */
function pflegeGroesseKurz() {
  const wurzel = document.documentElement;
  if (!wurzel.classList.contains('dp-kauf-nur-groesse')) {
    if (!wurzel.classList.contains('dp-kauf-nur-farbe')) wurzel.classList.remove('dp-kauf-hinweis-kurz');
    return;
  }
  const hinweis = document.querySelector('.dp-kauffenster .product-form-buttons > .dp-lieferung');
  const breite = hinweis ? Math.round(hinweis.getBoundingClientRect().width) : 0;
  if (!(breite > 0)) return;
  wurzel.classList.toggle('dp-kauf-hinweis-kurz', breite < HINWEIS_LANG);
}

/*
 * Nur Farbe: hat der Artikel genau EINE Auswahl und ist die eine Farbwahl (Farbpunkte,
 * mindestens zwei), steht sie - wenn sie Platz hat - links in der Kaufzeile:
 * [Farben] [Lieferhinweis] [Mengenteil + Knopf] (Stilblatt, Abschnitt "Nur Farbe").
 *
 * STABIL ENTSCHIEDEN: ob die Farben in die Zeile passen, wird sofort beim Aufbau und bei
 * jeder Aenderung der Fensterbreite gemessen - nicht erst, wenn der Lieferhinweis nach der
 * Farbwahl erscheint. Gerechnet wird mit dem Platz des Hinweises in seiner KURZFORM:
 *   Platz = Zeilenbreite - Farbpunkte - Mengenteil/Knopf - zwei Spaltenabstaende
 *   Platz >= HINWEIS_MIN  -> Farben stehen in der Zeile (Marke dp-kauf-farbe-unten)
 *   Platz <  HINWEIS_LANG -> der Hinweis zeigt die Kurzform (Marke dp-kauf-hinweis-kurz)
 *   sonst                 -> Farben wie bisher in der Zeile darueber
 * Die Marken sitzen auf <html> (das Theme zeichnet den Abschnitt beim Variantenwechsel neu
 * und nimmt dem Fenster dabei Klassen). Die Messung haengt nicht von der aktuellen
 * Anordnung ab - es gibt keine Rueckkopplung, und beim Wahlen einer Farbe aendert sich nichts.
 * Konfigurator-Fenster (transfer, imprint, solid, layer) sind ausgenommen.
 */
const HINWEIS_MIN = 72;
const HINWEIS_LANG = 120;
const FARBE_AUSNAHMEN = '.dp-kauffenster--transfer, .dp-kauffenster--imprint, .dp-kauffenster--solid, .dp-kauffenster--layer';
let farbeKapsel = 0;
let farbeBreite = -1;
let farbeUhr = 0;
let farbeBeobachtet = null;
let farbeLaeuft = null;

/** Misst, wie viel Platz der Hinweis neben den Farben hat; null, solange etwas fehlt. */
function messeFarbPlatz(kauf, bild) {
  const inhalt = kauf.querySelector(':scope > .group-block-content');
  const feld = bild.querySelector('.variant-option--swatches');
  const spanne =
    kauf.querySelector('.product-form-buttons > span.dp-kaufspanne') ||
    kauf.querySelector('.product-form-buttons add-to-cart-component')?.closest('.product-form-buttons > span');
  if (!inhalt || !feld || !spanne) return null;
  const etiketten = feld.querySelectorAll('.variant-option__button-label');
  if (etiketten.length < 2) return null;

  const stil = getComputedStyle(inhalt);
  const zeile = inhalt.clientWidth - (parseFloat(stil.paddingLeft) || 0) - (parseFloat(stil.paddingRight) || 0);
  if (!(zeile > 0)) return null;

  /* Die Punkte: vom linken Rand des ersten bis zum rechten des letzten Etiketts, dazu die Luft rechts fuer den Ring. */
  const erstes = etiketten[0].getBoundingClientRect();
  const letztes = etiketten[etiketten.length - 1].getBoundingClientRect();
  const farben = letztes.right - erstes.left + (parseFloat(getComputedStyle(feld).paddingRight) || 0);
  if (!(farben > 0)) return null;

  /* Mengenteil und Knopf als eine Kapsel: vom linken Rand des Mengenteils bis zum rechten des Knopfs. */
  const menge = kauf.querySelector('.product-form-buttons .quantity-selector-wrapper');
  const kapsel = spanne.getBoundingClientRect();
  const mengeBox = menge ? menge.getBoundingClientRect() : null;
  const gemessen = mengeBox && mengeBox.width > 0 ? kapsel.right - mengeBox.left : kapsel.width;
  /*
   * Gilt nur aus der Anordnung "darueber" (dort ist die Kaufzeile unbeengt): in der engen
   * Zeile koennte das Raster die Kapsel zusammendruecken und die Messung verfaelschen. Dann
   * gilt der zuletzt gemessene Wert - die Kapsel hat sonst keinen Grund, ihre Breite zu aendern.
   */
  if (gemessen > 0 && (farbeKapsel === 0 || !document.documentElement.classList.contains('dp-kauf-farbe-unten'))) {
    farbeKapsel = gemessen;
  }
  const kaufbreite = farbeKapsel;
  if (!(kaufbreite > 0)) return null;

  const abstand = window.matchMedia('(max-width: 749px)').matches ? 12 : 8;
  return zeile - farben - kaufbreite - 2 * abstand;
}

/** Ordnet die Farbzeile neu an - bewegt: Fenster und Farbwahl fahren auf ihren neuen Platz. */
function wechsleFarbzeile(kauf, aendere) {
  const bild = kauf ? kauf.querySelector('variant-picker') : null;
  const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!bild || ruhig || !kauf.offsetWidth || !('animate' in kauf)) {
    aendere();
    return;
  }
  if (farbeLaeuft) {
    for (const a of farbeLaeuft) a.cancel();
    farbeLaeuft = null;
  }
  const hoeheVon = kauf.getBoundingClientRect().height;
  const von = bild.getBoundingClientRect();
  aendere();
  const hoeheBis = kauf.getBoundingClientRect().height;
  const bis = bild.getBoundingClientRect();
  const dx = von.left - bis.left;
  const dy = von.top - bis.top;
  const bewegungen = [];
  const kurve = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
    bewegungen.push(
      bild.animate(
        [{ transform: 'translate(' + dx + 'px, ' + dy + 'px)' }, { transform: 'translate(0, 0)' }],
        { duration: 400, easing: kurve }
      )
    );
  }
  if (Math.abs(hoeheVon - hoeheBis) > 1) {
    bewegungen.push(kauf.animate([{ height: hoeheVon + 'px' }, { height: hoeheBis + 'px' }], { duration: 400, easing: kurve }));
  }
  if (bewegungen.length) {
    farbeLaeuft = bewegungen;
    Promise.all(bewegungen.map((a) => a.finished.catch(() => {}))).then(() => {
      if (farbeLaeuft === bewegungen) farbeLaeuft = null;
    });
  }
}

function pflegeNurFarbe(weich) {
  const kauf = document.querySelector('.dp-kauffenster');
  const bild = kauf ? kauf.querySelector('variant-picker') : null;
  /* Mitten im Neuzeichnen fehlt der Waehler kurz: dann bleiben die Marken, wie sie sind. */
  if (!bild) return;
  const wurzel = document.documentElement;
  const optionen = bild.querySelectorAll('.variant-option');
  const nur =
    !kauf.matches(FARBE_AUSNAHMEN) &&
    optionen.length === 1 &&
    optionen[0].classList.contains('variant-option--swatches') &&
    optionen[0].querySelectorAll('.variant-option__button-label').length > 1;

  if (!nur) {
    wurzel.classList.remove('dp-kauf-nur-farbe', 'dp-kauf-farbe-unten');
    /* Die Kurzform gehoert dann der Groessenzeile (pflegeGroesseKurz). */
    if (!wurzel.classList.contains('dp-kauf-nur-groesse')) wurzel.classList.remove('dp-kauf-hinweis-kurz');
    return;
  }
  wurzel.classList.add('dp-kauf-nur-farbe');

  const gemessen = messeFarbPlatz(kauf, bild);
  if (gemessen === null) return;
  /* Ganze Punkte: Bruchteile wuerden an den Schwellen (72, 120) sonst zufaellig kippen. */
  const platz = Math.round(gemessen);
  const unten = platz >= HINWEIS_MIN;
  const kurz = unten && platz < HINWEIS_LANG;
  const hatUnten = wurzel.classList.contains('dp-kauf-farbe-unten');
  const aendere = () => {
    wurzel.classList.toggle('dp-kauf-farbe-unten', unten);
    wurzel.classList.toggle('dp-kauf-hinweis-kurz', kurz);
  };
  if (weich && hatUnten !== unten) wechsleFarbzeile(kauf, aendere);
  else aendere();
}

/* Neu entscheiden, wenn sich die Breite des Fensters aendert (Fenstergroesse, Drehen) - kurz beruhigt, damit Fahrten nicht flackern. */
function beobachteFarbbreite() {
  const kauf = document.querySelector('.dp-kauffenster');
  const inhalt = kauf ? kauf.querySelector(':scope > .group-block-content') : null;
  if (!inhalt || inhalt === farbeBeobachtet) return;
  farbeBeobachtet = inhalt;
  if (!('ResizeObserver' in window)) return;
  new ResizeObserver(() => {
    const breite = Math.round(inhalt.clientWidth);
    if (breite === farbeBreite) return;
    farbeBreite = breite;
    window.clearTimeout(farbeUhr);
    farbeUhr = window.setTimeout(() => {
      pflegeNurFarbe(true);
      pflegeGroesseKurz();
    }, 120);
  }).observe(inhalt);
}

function starte() {
  pflegeNurGroesse();
  pflegeNurFarbe(false);
  beobachteFarbbreite();
  window.addEventListener('orientationchange', () => {
    window.clearTimeout(farbeUhr);
    farbeUhr = window.setTimeout(() => pflegeNurFarbe(true), 300);
  });
  document.addEventListener(
    'change',
    (ereignis) => {
      const ziel = ereignis.target;
      if (!(ziel instanceof Element) || !ziel.closest('variant-picker')) return;
      for (const wann of [0, 300, 1200]) window.setTimeout(pflegeNurGroesse, wann);
      /* Die Farbzeile bleibt, wie sie ist: nur die Beobachtung an ein neu gezeichnetes Fenster haengen. */
      for (const wann of [300, 1200]) window.setTimeout(beobachteFarbbreite, wann);
    },
    true
  );
  richteBeschreibung();
  halteReiterOffen();
  zeigeNachBildern();
  holeZurueckNachWechsel();

  /*
   * Die Huelle des Kaufknopfs bekommt ihre Klasse einmalig - das Stilblatt
   * fand sie frueher ueber :has(add-to-cart-component), und jede
   * :has()-Regel im Fenster verteuert dessen Animationen.
   */
  for (const spanne of document.querySelectorAll('.dp-kauffenster .product-form-buttons > span')) {
    if (spanne.querySelector('add-to-cart-component')) spanne.classList.add('dp-kaufspanne');
  }

  const fenster = document.querySelector('.dp-kauffenster');
  if (fenster) begleiteWachstum(fenster);
  ruesteAus();

  /*
   * Hat der Besucher auf einer vorigen Seite die Leiste gewaehlt, beginnt
   * das Fenster hier gleich so - ohne Fahrt, am unteren Rand. Wohin es von
   * dort gehoert (an die Bildkante, aufgefahren), entscheidet die gewohnte
   * Lagepruefung, sobald die Bilder stehen. Die volle Hoehe wird vorher
   * gemessen, solange das Fenster noch ganz dasteht.
   */
  if (fenster && !BREIT.matches && gemerkteLeiste()) {
    hoeheVoll = fenster.getBoundingClientRect().height;
    angedockt = true;
    leisteAktiv = true;
    fenster.classList.add(ANGEDOCKT, KLEIN);
    fenster.style.setProperty('height', LEISTE_HOEHE + 'px');
    schwebe(fenster);
    const knopf = fenster.querySelector('.dp-kauffenster__zu');
    if (knopf) {
      knopf.setAttribute('aria-pressed', 'true');
      knopf.setAttribute('aria-label', LOESE_WORT);
    }
    fuelleLeiste();
  }

  /*
   * Die Leiste spiegelt den Kaufknopf: wird aus ihm der Stift (fehlende
   * Angabe) oder wieder der Wagen, zieht sie nach. Eingaben sind der
   * Ausloeser dafuer; die Pruefung selbst schreibt nur bei Abweichung.
   */
  for (const art of ['input', 'change']) {
    document.addEventListener(art, () => window.setTimeout(fuelleLeiste, 80), true);
  }
  document.addEventListener('dp:galerie-neu', ruesteAus);
  /*
   * Nach einem Variantenwechsel zeichnet das Theme neu und kann das Fenster
   * ersetzen - ein paar kurze, begrenzte Blicke ruesten es wieder aus.
   */
  document.addEventListener(
    'change',
    (ereignis) => {
      const ziel = ereignis.target;
      if (!(ziel instanceof Element) || !ziel.closest('variant-picker')) return;
      for (const wann of [200, 600, 1200, 2400]) window.setTimeout(ruesteAus, wann);
    },
    true
  );
  BREIT.addEventListener('change', () => {
    if (BREIT.matches && angedockt) schalteAndocken(false, false);
  });

  document.addEventListener('toggle', merkeEingriff, true);
  BREIT.addEventListener('change', richteBeschreibung);

  beobachteMenue();
  BREIT.addEventListener('change', schiebe);
  window.addEventListener('resize', schiebe, { passive: true });

  /*
   * Auf dem Handy scrollt das Fenster, am Rechner die Seitenhuelle - der
   * Lauscher haengt deshalb mit capture am Fenster und bekommt beides mit.
   */
  reserviere();
  begrenzeAufGalerie();
  beobachteFenster();
  fasseLaufBereich();
  beobachteAufbau();
  beobachteGrossansicht();
  beobachteGalerieWechsel();
  /*
   * Waechst die Bildergalerie nachtraeglich - Bilder und Ansichten ziehen
   * Sekunden nach dem Aufbau ein -, stimmt die Park-Entscheidung nicht mehr:
   * das Fenster stand am alten Bildende, teils ausserhalb des Bildschirms,
   * und fand erst beim ersten Scrollen an den unteren Rand. Der Beobachter
   * rechnet Lage und Platzreserve bei jeder Groessenaenderung sofort neu;
   * nach einem Galerie-Austausch haengt er sich an die neue Galerie.
   */
  let galerieWache = null;
  let bewachte = null;
  const bewacheGalerie = () => {
    if (!('ResizeObserver' in window)) return;
    const galerie = sichtbareGalerie();
    if (!galerie || galerie === bewachte) return;
    if (galerieWache) galerieWache.disconnect();
    bewachte = galerie;
    galerieWache = new ResizeObserver(() => {
      // Die Platzreserve sofort, die Lage erst, wenn die Galerie steht.
      reserviere();
      pruefeLageSpaeter();
    });
    galerieWache.observe(galerie);
    /*
     * Eine frisch angehaengte Galerie hat eine eigene Hoehe - die Lage muss
     * einmal nachgerechnet werden, auch wenn der Beobachter sich noch nicht
     * gemeldet hat.
     */
    reserviere();
    pruefeLageSpaeter();
  };
  bewacheGalerie();
  document.addEventListener('dp:galerie-neu', bewacheGalerie);

  /*
   * Beim Wechsel der Farbe zeichnet Shopify den Produktabschnitt neu: die
   * Galerie wird durch eine andere ersetzt, und der Beobachter haengt danach
   * an einem Element, das nicht mehr im Bild steht. Gemeldet wird der Tausch
   * nicht zu einem festen Zeitpunkt - die neue Galerie steht mal sofort da,
   * mal erst, wenn ihre Bilder ihre Masse kennen. Deshalb ein paar kurze,
   * begrenzte Blicke nach jeder Auswahl; jeder haengt den Beobachter um,
   * sobald eine andere Galerie sichtbar ist.
   */
  document.addEventListener(
    'change',
    (ereignis) => {
      const ziel = ereignis.target;
      if (!(ziel instanceof Element) || !ziel.closest('variant-picker')) return;

      /*
       * Nur umhaengen: bewacheGalerie meldet sich von selbst, wenn dabei
       * tatsaechlich eine andere Galerie im Bild steht. Wuerde hier bei
       * jedem Blick die Lage angemeldet, verschoebe der letzte Blick die
       * Entscheidung um Sekunden.
       */
      for (const wann of [60, 200, 500, 900, 1500, 2400]) {
        window.setTimeout(bewacheGalerie, wann);
      }
    },
    true
  );

  haltAmBildende();
  /*
   * Nur das Rollen der Seite zaehlt. Mit capture kommen hier auch die
   * Rollereignisse jedes rollbaren Kastens an - der Warenkorb-Schublade,
   * des Personalisiert-Fensters, des eigenen Laufs -, und jedes davon liess
   * das Fenster seine Lage neu suchen: es schwebte dann an einen anderen
   * Platz, waehrend jemand im Warenkorb rollte.
   */
  window.addEventListener(
    'scroll',
    (ereignis) => {
      if (ereignis.target === document) haltAmBildende();
    },
    { passive: true, capture: true }
  );
  window.addEventListener('resize', haltAmBildende, { passive: true });
  window.addEventListener('resize', reserviere, { passive: true });
  window.addEventListener('resize', pflegeModusZeichen, { passive: true });
  window.addEventListener('resize', begrenzeAufGalerie, { passive: true });
  window.addEventListener('load', begrenzeAufGalerie);
  BREIT.addEventListener('change', fasseLaufBereich);
  BREIT.addEventListener('change', haltAmBildende);
  BREIT.addEventListener('change', reserviere);
  BREIT.addEventListener('change', begrenzeAufGalerie);
}

/*
 * Zieht der Kunde ein mehrzeiliges Feld am Anfasser groesser, waechst es
 * nach unten - steht es zuunterst, aus dem sichtbaren Teil des Rollbereichs
 * hinaus, und es wirkte abgeschnitten (Jimmi, Oktober 2026). Solange gezogen
 * wird, rollt der Bereich mit, sodass die Unterkante zu sehen bleibt.
 * Beobachtet wird nur das eine Feld, und nur waehrend des Ziehens.
 */
function begleiteGroessenzug(ereignis) {
  const feld = ereignis.target instanceof Element ? ereignis.target.closest('.dp-kauffenster textarea') : null;
  if (!(feld instanceof HTMLTextAreaElement) || typeof ResizeObserver !== 'function') return;
  const bereich = rollbereich();
  const lauf = bereich && bereich.contains(feld) ? bereich : feld.closest('.dp-kauffenster__texte, .dp-kauffenster__lauf');
  if (!(lauf instanceof HTMLElement)) return;
  const beobachter = new ResizeObserver(() => {
    const unten = feld.getBoundingClientRect().bottom + 12;
    const sichtbar = lauf.getBoundingClientRect().bottom;
    if (unten > sichtbar) lauf.scrollTop += unten - sichtbar;
  });
  beobachter.observe(feld);
  const ende = () => {
    beobachter.disconnect();
    window.removeEventListener('pointerup', ende, true);
    window.removeEventListener('pointercancel', ende, true);
  };
  window.addEventListener('pointerup', ende, true);
  window.addEventListener('pointercancel', ende, true);
}
document.addEventListener('pointerdown', begleiteGroessenzug, { passive: true });

/*
 * Das Tablet im Hochformat: das Kauffenster endet oberhalb der Bildschirmtastatur.
 *
 * Dort steht das Fenster neben den Bildern (wie am Rechner), die Tastatur
 * schiebt aber nur das Sichtfeld zusammen, nicht das Layout-Fenster (iOS) -
 * die Bildschirmhoehe im Stilblatt (dvh) bleibt voll, und die untere Haelfte
 * des Fensters verschwand hinter der Tastatur. Die gehobene Eingabezeile
 * (dp-tastaturzeile.js) laeuft hier nicht, sie gilt nur fuer Handy hoch und
 * quer; das Querformat am Tablet hat genug Hoehe und bleibt unberuehrt.
 *
 * Erkannt wird die Tastatur wie dort am Fokus: Schreibpunkt in einem Feld des
 * Kauffensters UND das Sichtfeld deutlich kleiner als die volle Hoehe. Dann
 * bekommt das Fenster eine Obergrenze bis zur Unterkante des Sichtfelds
 * (--dp-kauf-frei, Stilblatt) und faehrt weich auf diese Hoehe; beim
 * Schliessen weich zurueck. Das Merkmal data-dp-kauf-sicht bleibt dafuer bis
 * zum Ende der Rueckfahrt stehen ("offen", danach "zu").
 */
const TABLET_HOCH = '(min-width: 750px) and (orientation: portrait) and (pointer: coarse)';
const SICHT_LUECKE = 120;
const SICHT_FAHRT = 400;
/** Kleiner darf das Fenster auch bei sehr niedrigem Sichtfeld nicht werden. */
const MINDESTHOEHE_TASTATUR = 200;
let sichtVoll = window.innerHeight;
let sichtOffen = false;
let sichtUhr = 0;
let sichtFeldUhr = 0;

function textFeldImFenster() {
  const a = document.activeElement;
  if (!(a instanceof HTMLElement) || !a.closest('.dp-kauffenster')) return false;
  if (a.isContentEditable || a instanceof HTMLTextAreaElement) return true;
  if (!(a instanceof HTMLInputElement)) return false;
  return !/^(checkbox|radio|button|submit|reset|range|color|file|image|hidden)$/.test(a.type);
}

/** Holt das fokussierte Feld in den sichtbaren Teil des Scrollbereichs - nur dort, nie die Seite. */
function holeFeldInSicht() {
  const a = document.activeElement;
  if (!(a instanceof HTMLElement) || !sichtOffen) return;
  const bereich = rollbereich();
  const lauf = bereich && bereich.contains(a) ? bereich : a.closest('.dp-kauffenster__texte, .dp-kauffenster__lauf');
  if (!(lauf instanceof HTMLElement)) return;
  const f = a.getBoundingClientRect();
  const l = lauf.getBoundingClientRect();
  if (f.bottom + 12 > l.bottom) lauf.scrollTop += f.bottom + 12 - l.bottom;
  else if (f.top - 12 < l.top) lauf.scrollTop -= l.top - (f.top - 12);
}

function pflegeKaufSicht() {
  const wurzel = document.documentElement;
  const kauf = document.querySelector('.dp-kauffenster');
  const v = window.visualViewport;
  const feld = !!kauf && !!v && window.matchMedia(TABLET_HOCH).matches && textFeldImFenster();
  if (!feld && !sichtOffen) sichtVoll = Math.max(window.innerHeight, v ? v.height + v.offsetTop : 0);

  const unten = v ? Math.round(v.offsetTop + v.height) : 0;
  if (feld && unten < sichtVoll - SICHT_LUECKE) {
    // Obergrenze: Unterkante des Sichtfelds minus Oberkante des Fensters minus Luft.
    const luft = parseFloat(getComputedStyle(wurzel).getPropertyValue('--dp-kauf-luft')) || 24;
    const frei = Math.max(MINDESTHOEHE_TASTATUR, Math.round(unten - kauf.getBoundingClientRect().top - luft));
    window.clearTimeout(sichtUhr);
    wurzel.style.setProperty('--dp-kauf-frei', frei + 'px');
    if (!sichtOffen) {
      sichtOffen = true;
      wurzel.setAttribute('data-dp-kauf-sicht', 'offen');
    }
    window.clearTimeout(sichtFeldUhr);
    sichtFeldUhr = window.setTimeout(holeFeldInSicht, SICHT_FAHRT + 40);
    return;
  }

  if (!sichtOffen) return;
  sichtOffen = false;
  wurzel.setAttribute('data-dp-kauf-sicht', 'zu');
  wurzel.style.removeProperty('--dp-kauf-frei');
  window.clearTimeout(sichtUhr);
  sichtUhr = window.setTimeout(() => {
    if (!sichtOffen) wurzel.removeAttribute('data-dp-kauf-sicht');
  }, SICHT_FAHRT + 80);
}

document.addEventListener('focusin', () => window.setTimeout(pflegeKaufSicht, 0));
document.addEventListener('focusout', () => window.setTimeout(pflegeKaufSicht, 0));
window.addEventListener('resize', pflegeKaufSicht, { passive: true });
window.addEventListener('orientationchange', () => {
  sichtVoll = 0;
  window.setTimeout(pflegeKaufSicht, 300);
});
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', pflegeKaufSicht, { passive: true });
  window.visualViewport.addEventListener('scroll', pflegeKaufSicht, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starte, { once: true });
} else {
  starte();
}
